import { 
  BookmarkSquareIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  ChartBarIcon,
  DocumentMagnifyingGlassIcon,
  Squares2X2Icon,
  TableCellsIcon,
  CubeIcon,
} from '@heroicons/react/24/outline';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { BottomNavigationBar } from '../../components/BottomNavigationBar';
import { MarkdownText } from '../../components/chat/MarkdownText';
import { ExecutionPlanPanel } from '../../components/ExecutionPlanPanel';
import { PageHeader } from '../../components/PageHeader';
import { StageNavigationBar } from '../../components/StageNavigationBar/StageNavigationBar';
import { STAGE_PAGE_WIDTH_CLASS } from '../../constants/layout';
import {
  BACK_BUTTON_LABEL,
  CONTINUE_BUTTON_LABEL,
} from '../../constants/navigation';
import { capabilityMappingService } from '../../services/capabilityMappingService';
import { capabilityRecommendationService } from '../../services/capabilityRecommendationService';
import { craService, CRAAnalysisItem } from '../../services/craService';
import { projectService } from '../../services/projectService';
import {
  ApplicationMatch,
  CapabilityHierarchyLevel,
  CapabilityMapping,
  CapabilityMappingFilters,
  CapabilityMappingResponse,
  CapabilityMappingSelectionResponse,
  CapabilityPath,
} from '../../types/capabilityMapping';
import {
  CapabilityRecommendationSource,
  CapabilityRecommendationResponse,
} from '../../types/capabilityRecommendation';
import { ProjectItem, ProjectStatus, ProjectType } from '../../types/project';
import { logger } from '../../utils/logger';

// Score thresholds for analysis results
const GOOD_SCORE_THRESHOLD = 80;
const MEDIUM_SCORE_THRESHOLD = 60;

// Number of capability mapping requirements shown per page
const MAPPINGS_PER_PAGE = 1;

// Maximum number of top-ranked matching applications shown per requirement
const MAX_APPS_PER_REQUIREMENT = 2;

// Character budget (~2-3 sentences) for a collapsed application description
const APPLICATION_DESCRIPTION_PREVIEW_LENGTH = 180;

// localStorage key prefix for persisting an unsaved generated capability mapping
const CAPABILITY_MAPPING_DRAFT_KEY = 'capabilityMappingDraft';

// localStorage key prefix for the context signature captured when analysis ran
const CRA_ANALYSIS_CONTEXT_SIGNATURE_KEY = 'craAnalysisContextSignature';

// localStorage key prefix for persisting an unconfirmed capability recommendation
const CAPABILITY_RECOMMENDATION_DRAFT_KEY = 'capabilityRecommendationDraft';
const CONFIRMED_CAPABILITY_RECOMMENDATION_CACHE_KEY =
  'confirmedCapabilityRecommendation';

const CAPABILITY_PATH_PLACEHOLDER = 'N/A';
const PORTFOLIO_SEGMENT_SEPARATOR = '/';

// Capability mapping review actions and the selection statuses they produce.
const ACTION_ACCEPT = 'accept';
const ACTION_SKIP = 'skip';
const SELECTION_STATUS_ACCEPTED = 'accepted';
const SELECTION_STATUS_SKIPPED = 'skipped';

type CapabilityMappingAction = typeof ACTION_ACCEPT | typeof ACTION_SKIP;

// User-facing messaging for capability mapping export failures.
const EXPORT_SELECTIONS_LOG_MESSAGE = 'Failed to export selections:';
const EXPORT_SELECTIONS_ERROR_MESSAGE =
  'Failed to save selections to context';
const compareAlphabetically = (left: string, right: string) =>
  left.localeCompare(right);

// Matches an http(s) URL, stopping before whitespace or a closing parenthesis
const URL_PATTERN = /(https?:\/\/[^\s)]+)/g;

/** Shorten a URL to a compact, readable label (host + first path segment). */
const shortenUrl = (url: string): string => {
  try {
    const parsed = new URL(url);
    const firstSegment = parsed.pathname.split('/').filter(Boolean)[0];
    return firstSegment
      ? `${parsed.hostname}/${firstSegment}/\u2026`
      : `${parsed.hostname}/\u2026`;
  } catch {
    return url;
  }
};

/** Render text with any embedded URLs as compact, wrapping links. */
const renderTextWithLinks = (text: string): React.ReactNode =>
  text.split(URL_PATTERN).map((segment, index) => {
    if (/^https?:\/\//.test(segment)) {
      return (
        <a
          key={`url-${index}`}
          href={segment}
          target="_blank"
          rel="noopener noreferrer"
          title={segment}
          className="text-[#336094] underline underline-offset-2 break-all hover:text-[#2a4d7a]"
        >
          {shortenUrl(segment)}
        </a>
      );
    }
    return <React.Fragment key={`text-${index}`}>{segment}</React.Fragment>;
  });

type PortfolioTreeNode = {
  name: string;
  path: string;
  children: PortfolioTreeNode[];
};

type CapabilityRecommendationSourceGroup = {
  documentTitle: string;
  details: string[];
};

type ConfirmedCapabilityRecommendationCache = {
  capabilityRecommendation: CapabilityRecommendationResponse;
  selectedApplications: ApplicationMatch[];
  savedFile: string;
};

const groupRecommendationSources = (
  sources: CapabilityRecommendationSource[],
): CapabilityRecommendationSourceGroup[] => {
  const groupedSources = new Map<string, Set<string>>();

  sources.forEach((source) => {
    const details = groupedSources.get(source.document_title) ?? new Set<string>();
    details.add(source.detail);
    groupedSources.set(source.document_title, details);
  });

  return Array.from(groupedSources.entries()).map(([documentTitle, details]) => ({
    documentTitle,
    details: Array.from(details),
  }));
};

const expandPortfolioHierarchy = (portfolioPaths: string[]) => {
  const expandedPortfolios = new Set<string>();

  portfolioPaths.forEach((portfolioPath) => {
    const normalizedPath = portfolioPath.trim();
    if (!normalizedPath) {
      return;
    }

    const segments = normalizedPath
      .split(PORTFOLIO_SEGMENT_SEPARATOR)
      .map((segment) => segment.trim())
      .filter(Boolean);

    for (let index = 1; index <= segments.length; index += 1) {
      expandedPortfolios.add(
        segments.slice(0, index).join(PORTFOLIO_SEGMENT_SEPARATOR),
      );
    }
  });

  return Array.from(expandedPortfolios).sort(compareAlphabetically);
};

const buildPortfolioTree = (portfolioPaths: string[]): PortfolioTreeNode[] => {
  const rootNodes: PortfolioTreeNode[] = [];

  portfolioPaths.forEach((portfolioPath) => {
    const segments = portfolioPath
      .split(PORTFOLIO_SEGMENT_SEPARATOR)
      .map((segment) => segment.trim())
      .filter(Boolean);

    let currentPath = '';
    let currentLevel = rootNodes;

    segments.forEach((segment) => {
      currentPath = currentPath
        ? `${currentPath}${PORTFOLIO_SEGMENT_SEPARATOR}${segment}`
        : segment;

      let node = currentLevel.find((candidate) => candidate.path === currentPath);
      if (!node) {
        node = {
          name: segment,
          path: currentPath,
          children: [],
        };
        currentLevel.push(node);
      }

      currentLevel = node.children;
    });
  });

  const sortTree = (nodes: PortfolioTreeNode[]) => {
    nodes.sort((left, right) => compareAlphabetically(left.name, right.name));
    nodes.forEach((node) => sortTree(node.children));
  };

  sortTree(rootNodes);
  return rootNodes;
};

const collectPortfolioBranchPaths = (
  node: PortfolioTreeNode,
): string[] => [
  node.path,
  ...node.children.flatMap((childNode) => collectPortfolioBranchPaths(childNode)),
];

/**
 * CRAScreen - Context Review and Analysis interface
 * AI-powered analysis of uploaded context with tabbed results
 */
export const CRAScreen: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();

  // State management
  const [project, setProject] = useState<ProjectItem | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [analysisResults, setAnalysisResults] = useState<CRAAnalysisItem[]>([]);
  const [analysisBaselineSignature, setAnalysisBaselineSignature] =
    useState<string | null>(null);
  const [currentContextSignature, setCurrentContextSignature] =
    useState<string | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');
  const [selectedDetails, setSelectedDetails] = useState<string | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isAnalysisDetailsOpen, setIsAnalysisDetailsOpen] = useState(false);
  
  // Capability Mapping state
  const [capabilityMapping, setCapabilityMapping] = useState<CapabilityMappingResponse | null>(null);
  const [isGeneratingCapabilityMapping, setIsGeneratingCapabilityMapping] = useState(false);
  const [capabilityMappingError, setCapabilityMappingError] = useState<string | null>(null);
  const [acceptedApps, setAcceptedApps] = useState<Set<string>>(new Set());
  const [skippedApps, setSkippedApps] = useState<Set<string>>(new Set());
  const [selectionSaving, setSelectionSaving] = useState<string | null>(null);
  const [selectionReasons, setSelectionReasons] = useState<Map<string, string>>(new Map());
  const [isExportingSelections, setIsExportingSelections] = useState(false);
  const [exportSuccess, setExportSuccess] = useState<string | null>(null);

  // Project-level EBM capability recommendation state
  const [capabilityRecommendation, setCapabilityRecommendation] =
    useState<CapabilityRecommendationResponse | null>(null);
  const [isGeneratingRecommendation, setIsGeneratingRecommendation] =
    useState(false);
  const [isConfirmingRecommendation, setIsConfirmingRecommendation] =
    useState(false);
  const [recommendationError, setRecommendationError] = useState<string | null>(null);
  const [isOverrideOpen, setIsOverrideOpen] = useState(false);
  const [overrideSearch, setOverrideSearch] = useState('');
  const [overridePath, setOverridePath] = useState<CapabilityPath | null>(null);
  const [overrideDefinition, setOverrideDefinition] = useState<string | null>(null);
  const [overrideRationale, setOverrideRationale] = useState('');
  const [confirmedRecommendationFile, setConfirmedRecommendationFile] = useState<string | null>(null);
  const [capabilityApplications, setCapabilityApplications] = useState<ApplicationMatch[]>([]);
  const [selectedCapabilityApplications, setSelectedCapabilityApplications] =
    useState<ApplicationMatch[]>([]);
  const [applicationListSearch, setApplicationListSearch] = useState('');
  const [isLoadingCapabilityApplications, setIsLoadingCapabilityApplications] =
    useState(false);
  const [isSavingCapabilityApplications, setIsSavingCapabilityApplications] =
    useState(false);
  const [isLoadingSelectedCapabilityDefinition, setIsLoadingSelectedCapabilityDefinition] =
    useState(false);
  const capabilityDefinitionRequestId = useRef(0);
  const [expandedRecommendationSources, setExpandedRecommendationSources] =
    useState<Set<string>>(new Set());
  const [expandedApplicationDescriptions, setExpandedApplicationDescriptions] =
    useState<Set<string>>(new Set());

  // Portfolio selector state
  const [portfolios, setPortfolios] = useState<string[]>([]);
  const [selectedPortfolios, setSelectedPortfolios] = useState<string[]>([]);
  const [appliedPortfolios, setAppliedPortfolios] = useState<string[]>([]);
  const [isPortfolioDropdownOpen, setIsPortfolioDropdownOpen] = useState(false);
  const [collapsedPortfolioNodes, setCollapsedPortfolioNodes] = useState<Set<string>>(new Set());
  const portfolioDropdownRef = useRef<HTMLDivElement>(null);
  const [capabilityHierarchy, setCapabilityHierarchy] = useState<CapabilityHierarchyLevel[]>([]);
  const [capabilities, setCapabilities] = useState<string[]>([]);
  const [selectedCapabilities, setSelectedCapabilities] = useState<string[]>([]);
  const [appliedCapabilities, setAppliedCapabilities] = useState<string[]>([]);
  const [isCapabilityDropdownOpen, setIsCapabilityDropdownOpen] = useState(false);
  const [collapsedCapabilityLevels, setCollapsedCapabilityLevels] = useState<Set<string>>(new Set());
  const [collapsedCapabilityNodes, setCollapsedCapabilityNodes] = useState<Set<string>>(new Set());
  const capabilityDropdownRef = useRef<HTMLDivElement>(null);
  const [subcategories, setSubcategories] = useState<string[]>([]);
  const [selectedSubcategories, setSelectedSubcategories] = useState<string[]>([]);
  const [appliedSubcategories, setAppliedSubcategories] = useState<string[]>([]);

  const capabilityOverrideOptions = useMemo(
    () => capabilityHierarchy.flatMap((level) =>
      level.capabilities.flatMap((capability) => {
        const basePath: CapabilityPath = {
          l1: level.name,
          l2: capability.name,
        };
        const subcategoryPaths = capability.subcategories.map((subcategory) => ({
          ...basePath,
          l3: subcategory.name,
        }));
        return subcategoryPaths.length > 0 ? subcategoryPaths : [basePath];
      }),
    ),
    [capabilityHierarchy],
  );

  // Quality assessment panel state
  const [showGroundednessDetails, setShowGroundednessDetails] = useState(false);

  // Capability mapping pagination state
  const [capabilityMappingPage, setCapabilityMappingPage] = useState(1);

  // Modal state for reason input
  const [reasonModalOpen, setReasonModalOpen] = useState(false);
  const [reasonModalAction, setReasonModalAction] =
    useState<CapabilityMappingAction>(ACTION_ACCEPT);
  const [reasonText, setReasonText] = useState('');
  const [reasonModalData, setReasonModalData] = useState<{
    requirementTitle: string;
    requirementDescription: string | undefined;
    capabilityPath: { l1: string; l2: string; l3?: string; l4?: string; l5?: string };
    app: {
      app_number: string;
      name: string;
      portfolio: string;
      match_level: string;
      functional_description?: string;
    };
  } | null>(null);

  /** Build a stable signature of the current project context documents. */
  const computeContextSignature =
    useCallback(async (): Promise<string | null> => {
      if (!projectId) {
        return null;
      }
      try {
        const items = await projectService.getProjectContext(
          parseInt(projectId, 10),
        );
        return items
          .map((item) => {
            if (item.type === 'ProjectContextUploadedDocument') {
              return `doc:${item.name}:${item.updated_at ?? ''}`;
            }
            if (item.type === 'ProjectContextReferenceDocument') {
              return `ref:${item.file_url}:${item.updated_at ?? ''}`;
            }
            return `instructions:${item.updated_at ?? ''}`;
          })
          .sort(compareAlphabetically)
          .join('|');
      } catch {
        return null;
      }
    }, [projectId]);

  /** Refresh the current context signature to detect a stale analysis. */
  const refreshContextSignature = useCallback(async () => {
    const signature = await computeContextSignature();
    if (signature !== null) {
      setCurrentContextSignature(signature);
    }
  }, [computeContextSignature]);

  /** Persist the current context as the analysis baseline (clears warning). */
  const markAnalysisFresh = useCallback(async () => {
    if (!projectId) {
      return;
    }
    const signature = await computeContextSignature();
    if (signature !== null) {
      setAnalysisBaselineSignature(signature);
      setCurrentContextSignature(signature);
      localStorage.setItem(
        `${CRA_ANALYSIS_CONTEXT_SIGNATURE_KEY}_${projectId}`,
        signature,
      );
    }
  }, [projectId, computeContextSignature]);

  /**
   * Load existing analysis or generate new one if none exists
   */
  const loadOrGenerateAnalysis = useCallback(async () => {
    if (!projectId) {
      return;
    }
    
    setIsAnalyzing(true);
    setError(null);
    
    try {
      const response = await craService.getOrGenerateAnalysis(Number.parseInt(projectId, 10));
      setAnalysisResults(response.items);
    } catch {
      setError('Failed to load or generate analysis');
    } finally {
      setIsAnalyzing(false);
    }
  }, [projectId]);

  /**
   * Force regenerate analysis (called by user action)
   */
  const regenerateAnalysis = useCallback(async () => {
    if (!projectId) {
      return;
    }
    
    setIsAnalyzing(true);
    setError(null);
    
    try {
      const response = await craService.generateAnalysis(Number.parseInt(projectId, 10));
      setAnalysisResults(response.items);
      await markAnalysisFresh();
    } catch {
      setError('Failed to regenerate analysis');
    } finally {
      setIsAnalyzing(false);
    }
  }, [projectId, markAnalysisFresh]);

  /**
   * Handle stage navigation
   */
  const handleStageClick = (_stageId: number, route: string) => {
    navigate(route);
  };

  /**
   * Fetch project data and start analysis on component mount.
   * Project loading is completed before the agent call so users immediately
   * see the analysis progress state while the CRA agent is running.
   */
  useEffect(() => {
    const fetchProjectAndStartAnalysis = async () => {
      if (!projectId) {
        setError('Project ID not found');
        setIsLoading(false);
        return;
      }

      try {
        const projectData = await projectService.getProject(Number.parseInt(projectId, 10));
        setProject(projectData);
        setError(null);
        setIsLoading(false);

        const recommendationDraft = localStorage.getItem(
          `${CAPABILITY_RECOMMENDATION_DRAFT_KEY}_${projectId}`,
        );
        if (recommendationDraft) {
          try {
            const draftRecommendation = JSON.parse(
              recommendationDraft,
            ) as CapabilityRecommendationResponse;
            setCapabilityRecommendation(draftRecommendation);
            // Load the catalogue applications for the restored (unconfirmed)
            // recommendation so the Application List is populated without
            // requiring the user to confirm the capability first.
            setIsLoadingCapabilityApplications(true);
            capabilityRecommendationService.getApplications(
              parseInt(projectId, 10),
              draftRecommendation.recommendation.capability_path,
            ).then(setCapabilityApplications).catch(() => {
              setCapabilityApplications([]);
            }).finally(() => setIsLoadingCapabilityApplications(false));
          } catch {
            localStorage.removeItem(
              `${CAPABILITY_RECOMMENDATION_DRAFT_KEY}_${projectId}`,
            );
          }
        } else {
          let confirmedCapability;
          try {
            confirmedCapability = await capabilityRecommendationService.getConfirmed(
              parseInt(projectId, 10),
            );
          } catch (confirmationError) {
            // Restore is best-effort; the client cache fallback below keeps the
            // confirmed capability visible even when the durable read fails.
            console.error(
              'Failed to restore confirmed capability:',
              confirmationError,
            );
          }
          if (confirmedCapability) {
            const restoredRecommendation = {
              project_id: confirmedCapability.project_id,
              recommendation: {
                capability_path: confirmedCapability.capability_path,
                capability_definition: confirmedCapability.capability_definition,
                rationale: confirmedCapability.rationale,
                evidence: [],
                similar_applications: [],
              },
              generated_at: '',
            };
            setCapabilityRecommendation(restoredRecommendation);
            localStorage.setItem(
              `${CONFIRMED_CAPABILITY_RECOMMENDATION_CACHE_KEY}_${projectId}`,
              JSON.stringify({
                capabilityRecommendation: restoredRecommendation,
                selectedApplications: confirmedCapability.selected_applications,
                savedFile: 'ebm_capability_recommendation.md',
              } satisfies ConfirmedCapabilityRecommendationCache),
            );
            setConfirmedRecommendationFile('ebm_capability_recommendation.md');
            setSelectedCapabilityApplications(
              confirmedCapability.selected_applications,
            );
            setIsLoadingCapabilityApplications(true);
            capabilityRecommendationService.getApplications(
              parseInt(projectId, 10),
              confirmedCapability.capability_path,
            ).then(setCapabilityApplications).catch((applicationError) => {
              setCapabilityApplications(confirmedCapability.selected_applications);
              const message = applicationError instanceof Error
                ? applicationError.message
                : 'Unknown error';
              setRecommendationError(`Failed to load applications: ${message}`);
            }).finally(() => setIsLoadingCapabilityApplications(false));
          } else {
            // Fall back to the client-side cache so the confirmed capability
            // persists across refreshes (like the Application Finder) even when
            // the durable read returns nothing or fails.
            const cachedConfirmation = localStorage.getItem(
              `${CONFIRMED_CAPABILITY_RECOMMENDATION_CACHE_KEY}_${projectId}`,
            );
            if (cachedConfirmation) {
              const cached = JSON.parse(
                cachedConfirmation,
              ) as ConfirmedCapabilityRecommendationCache
                | CapabilityRecommendationResponse;
              const isCompleteCache = 'capabilityRecommendation' in cached;
              const cachedRecommendation = isCompleteCache
                ? cached.capabilityRecommendation
                : cached;
              const cachedApplications = isCompleteCache
                ? cached.selectedApplications
                : [];
              const savedFile = isCompleteCache
                ? cached.savedFile
                : 'ebm_capability_recommendation.md';
              setCapabilityRecommendation(cachedRecommendation);
              setSelectedCapabilityApplications(cachedApplications);
              setConfirmedRecommendationFile(savedFile);
              setIsLoadingCapabilityApplications(true);
              capabilityRecommendationService.getApplications(
                parseInt(projectId, 10),
                cachedRecommendation.recommendation.capability_path,
              ).then(setCapabilityApplications).catch(() => {
                setCapabilityApplications(cachedApplications);
              }).finally(() => setIsLoadingCapabilityApplications(false));
            }
          }
        }

        // Load existing analysis or generate if none exists. Do not await this
        // before rendering the page, otherwise users only see the generic page
        // loader instead of the active CRA agent progress indicator.
        loadOrGenerateAnalysis().catch(() => {
          // Error already handled in loadOrGenerateAnalysis function
        });

        // Initialise the analysis staleness baseline from stored/current context
        const contextSignature = await computeContextSignature();
        if (contextSignature !== null) {
          setCurrentContextSignature(contextSignature);
          const baselineKey =
            `${CRA_ANALYSIS_CONTEXT_SIGNATURE_KEY}_${projectId}`;
          const storedBaseline = localStorage.getItem(baselineKey);
          if (storedBaseline) {
            setAnalysisBaselineSignature(storedBaseline);
          } else {
            setAnalysisBaselineSignature(contextSignature);
            localStorage.setItem(baselineKey, contextSignature);
          }
        }

        // Restore the Application Finder like ADR suggestions: the full agent
        // output lives in a client-side cache (localStorage). Prefer it so
        // un-interacted suggestions stay visible, then overlay the accept/skip
        // decisions saved server-side. When no cached output exists (e.g. a new
        // session), fall back to reconstructing the interacted apps from the
        // saved selections; the user re-runs Search Apps to refresh the rest.
        try {
          const draft = localStorage.getItem(
            `${CAPABILITY_MAPPING_DRAFT_KEY}_${projectId}`,
          );
          let hasCachedMapping = false;
          if (draft) {
            try {
              setCapabilityMapping(
                JSON.parse(draft) as CapabilityMappingResponse,
              );
              hasCachedMapping = true;
            } catch (parseError) {
              console.error(
                'Failed to restore capability mapping draft:',
                parseError,
              );
            }
          }

          const selections = await capabilityMappingService.getSelections(
            Number.parseInt(projectId, 10),
          );

          if (selections.length > 0) {
            const accepted = new Set<string>();
            const skipped = new Set<string>();
            const reasons = new Map<string, string>();
            
            // Group selections by requirement to reconstruct capability mappings
            const requirementMap = new Map<string, {
              requirement: string;
              capability_definition?: string;
              capability_path: CapabilityPath;
              applications: ApplicationMatch[];
            }>();
            
            selections.forEach((selection: CapabilityMappingSelectionResponse) => {
              const key = `${selection.requirement_title}-${selection.app_number}`;
              if (selection.status === SELECTION_STATUS_ACCEPTED) {
                accepted.add(key);
              } else if (selection.status === SELECTION_STATUS_SKIPPED) {
                skipped.add(key);
              }
              // Store reason for display
              if (selection.reason) {
                reasons.set(key, selection.reason);
              }
              
              // Group by requirement to reconstruct the mapping view
              const reqKey = selection.requirement_title;
              if (!requirementMap.has(reqKey)) {
                requirementMap.set(reqKey, {
                  requirement: selection.requirement_title,
                  capability_definition: selection.requirement_description,
                  capability_path: selection.capability_path,
                  applications: [],
                });
              }
              
              const reqData = requirementMap.get(reqKey)!;
              // Avoid duplicate apps
              if (!reqData.applications.some(a => a.app_number === selection.app_number)) {
                reqData.applications.push({
                  app_number: selection.app_number,
                  name: selection.app_name,
                  portfolio: selection.portfolio || '',
                  match_level: selection.match_level || '',
                  functional_description: selection.functional_description,
                });
              }
            });
            
            setAcceptedApps(accepted);
            setSkippedApps(skipped);
            setSelectionReasons(reasons);

            // Only rebuild the mapping from selections when no full agent output
            // is cached, otherwise the cached suggestions (including apps the
            // user has not interacted with) must remain visible.
            if (!hasCachedMapping) {
              const reconstructedMappings: CapabilityMapping[] = Array.from(
                requirementMap.values(),
              ).map(data => ({
                requirement: data.requirement,
                capability_definition: data.capability_definition,
                capability_path: data.capability_path,
                matching_applications: data.applications,
                gap_identified: data.applications.length === 0,
              }));

              if (reconstructedMappings.length > 0) {
                setCapabilityMapping({
                  project_id: parseInt(projectId, 10),
                  mappings: reconstructedMappings,
                });
              }
            }
          }
        } catch (loadError) {
          // Don't fail if selections can't be loaded
          logger.error('Failed to load capability mapping selections:', loadError);
        }
      } catch {
        setError('Failed to load project');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProjectAndStartAnalysis();

  }, [projectId, loadOrGenerateAnalysis, computeContextSignature]);

  // Re-check the context signature when the user returns to the tab so a
  // stale-analysis warning appears after context changes elsewhere.
  useEffect(() => {
    const handleFocus = () => {
      void refreshContextSignature();
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [refreshContextSignature]);

  /**
   * Fetch available portfolios for the dropdown selector
   */
  useEffect(() => {
    const fetchPortfolios = async () => {
      if (!projectId) {return;}
      try {
        const data = await capabilityMappingService.getPortfolios(
          Number.parseInt(projectId, 10),
        );
        const expandedPortfolios = expandPortfolioHierarchy(data);
        setPortfolios(expandedPortfolios);
        setSelectedPortfolios(expandedPortfolios); // Select all by default
        setAppliedPortfolios(expandedPortfolios);
      } catch (err) {
        logger.error('Failed to load portfolios:', err);
      }
    };
    fetchPortfolios();
  }, [projectId]);

  /**
   * Fetch the nested capability hierarchy for the tree selector.
   */
  useEffect(() => {
    const fetchCapabilityHierarchy = async () => {
      if (!projectId) {return;}
      try {
        const data = await capabilityMappingService.getCapabilityHierarchy(
          Number.parseInt(projectId, 10),
        );
        const nextCapabilities = data.flatMap((level) =>
          level.capabilities.map((capability) => capability.name),
        );
        const nextSubcategories = data.flatMap((level) =>
          level.capabilities.flatMap((capability) =>
            capability.subcategories.map((subcategory) => subcategory.name),
          ),
        );

        setCapabilityHierarchy(data);
        setCapabilities(nextCapabilities);
        setSubcategories(nextSubcategories);
        setSelectedCapabilities(nextCapabilities);
        setSelectedSubcategories(nextSubcategories);
        setAppliedCapabilities(nextCapabilities);
        setAppliedSubcategories(nextSubcategories);
      } catch (err) {
        logger.error('Failed to load capability hierarchy:', err);
      }
    };
    fetchCapabilityHierarchy();
  }, [projectId]);

  /**
   * Keep selected subcategories scoped to the selected capabilities.
   */
  useEffect(() => {
    if (capabilityHierarchy.length === 0) {
      return;
    }

    const includeAllCapabilities =
      selectedCapabilities.length === 0
      || selectedCapabilities.length === capabilities.length;
    const activeCapabilitySet = new Set(selectedCapabilities);
    const visibleSubcategories = capabilityHierarchy.flatMap((level) =>
      level.capabilities.flatMap((capability) =>
        includeAllCapabilities || activeCapabilitySet.has(capability.name)
          ? capability.subcategories.map((subcategory) => subcategory.name)
          : [],
      ),
    );

    setSubcategories(visibleSubcategories);
    setSelectedSubcategories((prev) =>
      prev.filter((subcategory) => visibleSubcategories.includes(subcategory)),
    );
  }, [capabilityHierarchy, capabilities.length, selectedCapabilities]);

  /**
   * Keep applied subcategories valid after capability selections change.
   */
  useEffect(() => {
    setAppliedSubcategories((prev) =>
      prev.filter((subcategory) => subcategories.includes(subcategory)),
    );
  }, [subcategories]);

  /**
   * Close portfolio dropdown when clicking outside
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        portfolioDropdownRef.current &&
        !portfolioDropdownRef.current.contains(event.target as Node)
      ) {
        setIsPortfolioDropdownOpen(false);
      }
      if (
        capabilityDropdownRef.current &&
        !capabilityDropdownRef.current.contains(event.target as Node)
      ) {
        setIsCapabilityDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /**
   * Handle opening details modal
   */
  const handleShowDetails = (details: string | null) => {
    if (details) {
      setSelectedDetails(details);
      setIsDetailsModalOpen(true);
    }
  };

  /**
   * Handle closing details modal
   */
  const handleCloseDetails = () => {
    setIsDetailsModalOpen(false);
    setSelectedDetails(null);
  };

  /**
   * Get score color based on score value
   */
  const getScoreColor = (score: number) => {
    if (score >= GOOD_SCORE_THRESHOLD) {
      return 'text-[#008557]'; // Green for good scores
    }
    if (score >= MEDIUM_SCORE_THRESHOLD) {
      return 'text-[#ffc600]'; // Yellow for medium scores
    }
    return 'text-red-500'; // Red for low scores
  };

  /**
   * Get score background color based on score value
   */
  const getScoreBgColor = (score: number) => {
    if (score >= GOOD_SCORE_THRESHOLD) {
      return 'bg-[#008557]'; // Green for good scores
    }
    if (score >= MEDIUM_SCORE_THRESHOLD) {
      return 'bg-[#ffc600]'; // Yellow for medium scores
    }
    return 'bg-red-500'; // Red for low scores
  };

  /** Build a concise project-level summary from the scored analysis segments. */
  const getProjectAnalysisSummary = () => {
    const totalScore = analysisResults.reduce(
      (sum, result) => sum + result.score,
      0,
    );
    const score = Math.round(totalScore / analysisResults.length);
    const rankedResults = [...analysisResults].sort((left, right) => (
      right.score - left.score
    ));
    const strongestArea = rankedResults[0];
    const improvementArea = rankedResults[rankedResults.length - 1];

    return {
      score,
      description: strongestArea.title === improvementArea.title
        ? `${strongestArea.title} is the only assessed project area.`
        : `${strongestArea.title} is the strongest assessed area, while `
          + `${improvementArea.title} needs additional project context.`,
      recommendation: improvementArea.details
        ?? `Prioritize improvements in ${improvementArea.title} before moving `
          + 'to the next design steps.',
    };
  };

  /**
   * Render analysis results as cards
   */
  const renderCardsView = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {analysisResults.map((result, index) => (
        <div key={`analysis-card-${result.title}-${index}`} className="bg-white rounded-lg p-6 border border-gray-200 hover:shadow-md transition-shadow">
          <div className="flex items-start justify-between mb-3">
            <h3 className="font-semibold text-gray-900">{result.title}</h3>
            <div className="flex items-center gap-1">
              <div className={`w-2 h-2 rounded-full ${getScoreBgColor(result.score)}`}></div>
              <span className={`text-lg font-bold ${getScoreColor(result.score)}`}>
                {result.score}
              </span>
            </div>
          </div>
          
          <p className="text-gray-600 text-sm mb-4 break-words">
            {renderTextWithLinks(result.explanation)}
          </p>
          
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">
              Score: {result.score}/100
            </span>
            {result.details ? (
              <button
                onClick={() => handleShowDetails(result.details || null)}
                className={`text-xs font-medium underline cursor-pointer ${
                  result.score < GOOD_SCORE_THRESHOLD
                    ? 'text-red-600 hover:text-red-700'
                    : 'text-[#336094] hover:text-[#2a4d7a]'
                }`}
              >
                {result.score < GOOD_SCORE_THRESHOLD ? 'Needs attention' : 'Details'}
              </button>
            ) : (
              result.score < GOOD_SCORE_THRESHOLD && (
                <span className="text-xs text-red-600 font-medium">
                  Needs attention
                </span>
              )
            )}
          </div>
        </div>
      ))}
    </div>
  );

  /**
   * Render analysis results as table
   */
  const renderTableView = () => (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Analysis Area
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Score
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Explanation
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {analysisResults.map((result, index) => (
              <tr key={`analysis-row-${result.title}-${index}`} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{result.title}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${getScoreBgColor(result.score)}`}></div>
                    <span className={`text-sm font-semibold ${getScoreColor(result.score)}`}>
                      {result.score}/100
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-600 max-w-md break-words">
                    {renderTextWithLinks(result.explanation)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {result.details ? (
                    <button
                      onClick={() => handleShowDetails(result.details || null)}
                      className={`text-xs font-medium underline cursor-pointer ${
                        result.score < GOOD_SCORE_THRESHOLD
                          ? 'text-red-600 hover:text-red-700'
                          : 'text-[#336094] hover:text-[#2a4d7a]'
                      }`}
                    >
                      {result.score < GOOD_SCORE_THRESHOLD ? 'Needs attention' : 'Details'}
                    </button>
                  ) : (
                    result.score < GOOD_SCORE_THRESHOLD && (
                      <span className="text-xs text-red-600 font-medium">
                        Needs attention
                      </span>
                    )
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  /**
   * Render analysis results content
   */
  const renderAnalysisResults = () => {
    if (isAnalyzing) {
      return (
        <div className="bg-white rounded-lg p-12 border border-gray-200">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#336094] mx-auto mb-4"></div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Analyzing Context</h3>
            <p className="text-gray-600">
              Please wait while we analyze your project context and generate insights...
            </p>
          </div>
        </div>
      );
    }

    if (analysisResults.length > 0) {
      return viewMode === 'cards' ? renderCardsView() : renderTableView();
    }

    return (
      <div className="bg-white rounded-lg p-12 border border-gray-200">
        <div className="text-center">
          <DocumentMagnifyingGlassIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Analysis Results</h3>
          <p className="text-gray-600 mb-4">
            Analysis will start automatically when you navigate to this page.
          </p>
        </div>
      </div>
    );
  };

  /**
   * Handle regenerate analysis with proper async handling
   */
  const handleRegenerateAnalysis = () => {
    // Async function wrapper for button onClick
    regenerateAnalysis().catch(() => {
      // Error already handled in regenerateAnalysis function
    });
  };

  // True when new context has been added since the analysis last ran
  const isAnalysisStale =
    analysisResults.length > 0 &&
    analysisBaselineSignature !== null &&
    currentContextSignature !== null &&
    analysisBaselineSignature !== currentContextSignature;

  const handleProceedToADRs = () => {
    if (!projectId) {
      return;
    }
    navigate(`/project/${projectId}/adrs`);
  };

  /** Generate a single EBM capability recommendation for the full project. */
  const handleGenerateCapabilityRecommendation = async () => {
    if (!projectId) {
      return;
    }

    setIsGeneratingRecommendation(true);
    capabilityDefinitionRequestId.current += 1;
    setRecommendationError(null);
    setConfirmedRecommendationFile(null);
    try {
      const result = await capabilityRecommendationService.generate(
        parseInt(projectId, 10),
      );
      setCapabilityRecommendation(result);
      setSelectedCapabilityApplications([]);
      setOverridePath(null);
      setOverrideDefinition(null);
      setOverrideRationale('');
      setIsOverrideOpen(false);
      // Store the agent's choice in the durable md by default; the architect
      // can change it manually or rerun the agent for another suggestion.
      try {
        const confirmation = await capabilityRecommendationService.confirm(
          parseInt(projectId, 10),
          result.recommendation.capability_path,
          result.recommendation.rationale,
        );
        setConfirmedRecommendationFile(confirmation.saved_file);
        localStorage.removeItem(
          `${CAPABILITY_RECOMMENDATION_DRAFT_KEY}_${projectId}`,
        );
        localStorage.setItem(
          `${CONFIRMED_CAPABILITY_RECOMMENDATION_CACHE_KEY}_${projectId}`,
          JSON.stringify({
            capabilityRecommendation: result,
            selectedApplications: [],
            savedFile: confirmation.saved_file,
          } satisfies ConfirmedCapabilityRecommendationCache),
        );
        void refreshContextSignature();
      } catch (confirmError) {
        // If the agent's path is not in the catalogue, keep it as an
        // unconfirmed draft so the architect can adjust it manually.
        console.error('Auto-confirm of capability failed:', confirmError);
        localStorage.setItem(
          `${CAPABILITY_RECOMMENDATION_DRAFT_KEY}_${projectId}`,
          JSON.stringify(result),
        );
      }
      await loadCapabilityApplications(result.recommendation.capability_path);
    } catch (generationError) {
      const message = generationError instanceof Error
        ? generationError.message
        : 'Unknown error';
      setRecommendationError(`Failed to generate capability recommendation: ${message}`);
    } finally {
      setIsGeneratingRecommendation(false);
    }
  };

  /** Confirm the recommendation or an architect-selected catalogue override. */
  const handleConfirmCapabilityRecommendation = async (
    selectedPath?: CapabilityPath,
  ) => {
    if (!projectId || !capabilityRecommendation) {
      return;
    }

    const isManualSelection = selectedPath !== undefined;
    const path = selectedPath ?? capabilityRecommendation.recommendation.capability_path;
    const manualComment = overrideRationale.trim();
    const rationale = isManualSelection
      ? `User manually selected.${manualComment ? ` User comment: ${manualComment}` : ''}`
      : undefined;
    const applicationsToConfirm = isManualSelection
      ? []
      : selectedCapabilityApplications;
    setIsConfirmingRecommendation(true);
    setRecommendationError(null);
    try {
      const result = await capabilityRecommendationService.confirm(
        parseInt(projectId, 10),
        path,
        rationale,
        applicationsToConfirm,
      );
      setConfirmedRecommendationFile(result.saved_file);
      localStorage.removeItem(`${CAPABILITY_RECOMMENDATION_DRAFT_KEY}_${projectId}`);
      setOverridePath(path);
      setOverrideDefinition(null);
      setOverrideRationale('');
      const confirmedRecommendation = {
        ...capabilityRecommendation,
        recommendation: {
          ...capabilityRecommendation.recommendation,
          capability_path: result.capability_path,
          capability_definition: result.capability_definition,
          rationale: rationale ?? capabilityRecommendation.recommendation.rationale,
        },
      };
      setCapabilityRecommendation(confirmedRecommendation);
      localStorage.setItem(
        `${CONFIRMED_CAPABILITY_RECOMMENDATION_CACHE_KEY}_${projectId}`,
        JSON.stringify({
          capabilityRecommendation: confirmedRecommendation,
          selectedApplications: applicationsToConfirm,
          savedFile: result.saved_file,
        } satisfies ConfirmedCapabilityRecommendationCache),
      );
      if (isManualSelection) {
        setSelectedCapabilityApplications([]);
      }
      setIsOverrideOpen(false);
      await loadCapabilityApplications(result.capability_path);
      void refreshContextSignature();
    } catch (confirmationError) {
      const message = confirmationError instanceof Error
        ? confirmationError.message
        : 'Unknown error';
      setRecommendationError(`Failed to confirm capability: ${message}`);
    } finally {
      setIsConfirmingRecommendation(false);
    }
  };

  /** Load an architect-selected capability without applying it until confirmation. */
  const handleCapabilitySelection = async (path: CapabilityPath) => {
    if (!projectId) {
      return;
    }

    const requestId = capabilityDefinitionRequestId.current + 1;
    capabilityDefinitionRequestId.current = requestId;
    setOverridePath(path);
    setOverrideDefinition(null);
    setIsLoadingSelectedCapabilityDefinition(true);
    setRecommendationError(null);
    // Preview the applications for the capability being browsed so the
    // Application List updates in real time as the architect clicks items.
    void loadCapabilityApplications(path);
    try {
      const result = await capabilityRecommendationService.getDefinition(
        parseInt(projectId, 10),
        path,
      );
      if (capabilityDefinitionRequestId.current === requestId) {
        setOverrideDefinition(result.capability_definition);
      }
    } catch (selectionError) {
      if (capabilityDefinitionRequestId.current === requestId) {
        const message = selectionError instanceof Error
          ? selectionError.message
          : 'Unknown error';
        setRecommendationError(`Failed to load capability description: ${message}`);
      }
    } finally {
      if (capabilityDefinitionRequestId.current === requestId) {
        setIsLoadingSelectedCapabilityDefinition(false);
      }
    }
  };

  /** Start a new capability change without retaining a prior pending selection. */
  const handleOpenCapabilityChange = () => {
    capabilityDefinitionRequestId.current += 1;
    setOverridePath(null);
    setOverrideDefinition(null);
    setOverrideRationale('');
    setOverrideSearch('');
    setIsOverrideOpen(true);
  };

  /** Load catalogue applications mapped directly to the selected capability. */
  const loadCapabilityApplications = async (path: CapabilityPath) => {
    if (!projectId) {
      return;
    }

    setIsLoadingCapabilityApplications(true);
    setRecommendationError(null);
    try {
      const applications = await capabilityRecommendationService.getApplications(
        parseInt(projectId, 10),
        path,
      );
      setCapabilityApplications(applications);
    } catch (applicationError) {
      const message = applicationError instanceof Error
        ? applicationError.message
        : 'Unknown error';
      setRecommendationError(`Failed to load applications: ${message}`);
    } finally {
      setIsLoadingCapabilityApplications(false);
    }
  };

  /** Toggle an application in the user-maintained capability application list. */
  const toggleCapabilityApplication = (application: ApplicationMatch) => {
    setSelectedCapabilityApplications((previous) => {
      const isSelected = previous.some(
        (item) => item.app_number === application.app_number,
      );
      return isSelected
        ? previous.filter((item) => item.app_number !== application.app_number)
        : [...previous, application];
    });
  };

  /** Persist user-selected applications in the confirmed capability context. */
  const saveCapabilityApplications = async () => {
    if (!projectId || !capabilityRecommendation) {
      return;
    }

    setIsSavingCapabilityApplications(true);
    setRecommendationError(null);
    try {
      const result = await capabilityRecommendationService.confirm(
        parseInt(projectId, 10),
        capabilityRecommendation.recommendation.capability_path,
        capabilityRecommendation.recommendation.rationale,
        selectedCapabilityApplications,
      );
      setConfirmedRecommendationFile(result.saved_file);
      localStorage.setItem(
        `${CONFIRMED_CAPABILITY_RECOMMENDATION_CACHE_KEY}_${projectId}`,
        JSON.stringify({
          capabilityRecommendation,
          selectedApplications: selectedCapabilityApplications,
          savedFile: result.saved_file,
        } satisfies ConfirmedCapabilityRecommendationCache),
      );
      void refreshContextSignature();
    } catch (saveError) {
      const message = saveError instanceof Error ? saveError.message : 'Unknown error';
      setRecommendationError(`Failed to save selected applications: ${message}`);
    } finally {
      setIsSavingCapabilityApplications(false);
    }
  };

  /**
   * Generate IT4IT capability mapping
   */
  const handleGenerateCapabilityMapping = async () => {
    if (!projectId) {
      return;
    }

    setIsGeneratingCapabilityMapping(true);
    setCapabilityMappingError(null);
    // Reset selection states on regenerate
    setAcceptedApps(new Set());
    setSkippedApps(new Set());
    setSelectionReasons(new Map());

    try {
      // Delete all existing selections before regenerating
      await capabilityMappingService.deleteAllSelections(Number.parseInt(projectId, 10));

      const buildFilter = (
        selectedValues: string[],
        availableValues: string[],
      ): string[] | undefined => {
        if (
          availableValues.length === 0 ||
          selectedValues.length === 0 ||
          selectedValues.length === availableValues.length
        ) {
          return undefined;
        }
        return selectedValues;
      };

      const filters: CapabilityMappingFilters = {
        portfolios: buildFilter(selectedPortfolios, portfolios),
        capabilities: buildFilter(selectedCapabilities, capabilities),
        subcategories: buildFilter(selectedSubcategories, subcategories),
      };

      const result = await capabilityMappingService.generateCapabilityMapping(
        Number.parseInt(projectId, 10),
        filters,
      );
      setAppliedPortfolios(selectedPortfolios);
      setAppliedCapabilities(selectedCapabilities);
      setAppliedSubcategories(selectedSubcategories);
      setCapabilityMapping(result);
      setCapabilityMappingPage(1);
      // Cache the full agent output (like ADR suggestions) so every suggestion
      // stays visible across navigation, refresh, and browser restarts; the
      // accept/skip decisions are overlaid from the server-side selections.
      try {
        localStorage.setItem(
          `${CAPABILITY_MAPPING_DRAFT_KEY}_${projectId}`,
          JSON.stringify(result),
        );
      } catch (storageError) {
        logger.error('Failed to persist capability mapping draft:', storageError);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      logger.error('Capability mapping error:', error);
      setCapabilityMappingError(`Failed to generate capability mapping: ${errorMessage}`);
    } finally {
      setIsGeneratingCapabilityMapping(false);
    }
  };

  /**
   * Open the reason modal for accepting an application
   */
  const handleAcceptApp = (
    requirementTitle: string,
    requirementDescription: string | undefined,
    capabilityPath: { l1: string; l2: string; l3?: string; l4?: string; l5?: string },
    app: {
      app_number: string;
      name: string;
      portfolio: string;
      match_level: string;
      functional_description?: string;
    },
  ) => {
    setReasonModalData({
      requirementTitle,
      requirementDescription,
      capabilityPath,
      app,
    });
    setReasonModalAction(ACTION_ACCEPT);
    setReasonText('');
    setReasonModalOpen(true);
  };

  const getCapabilityTreeSummary = () => {
    if (capabilities.length === 0) {
      return 'All capabilities';
    }

    if (selectedCapabilities.length === 0) {
      return 'All capabilities and subcategories';
    }

    const allCapabilitiesSelected = selectedCapabilities.length === capabilities.length;
    const allSubcategoriesSelected = selectedSubcategories.length === subcategories.length;

    if (allCapabilitiesSelected && allSubcategoriesSelected) {
      return 'All capabilities and subcategories';
    }

    return (
      `${selectedCapabilities.length}/${capabilities.length} capabilities, ` +
      `${selectedSubcategories.length}/${subcategories.length} subcategories`
    );
  };

  const getPortfolioTreeSummary = () => {
    if (portfolios.length === 0) {
      return 'All portfolios';
    }

    if (
      selectedPortfolios.length === 0
      || selectedPortfolios.length === portfolios.length
    ) {
      return 'All portfolios';
    }

    return `${selectedPortfolios.length}/${portfolios.length} portfolios selected`;
  };

  const portfolioTree = useMemo(
    () => buildPortfolioTree(portfolios),
    [portfolios],
  );

  const selectedPortfolioSet = useMemo(
    () => new Set(selectedPortfolios),
    [selectedPortfolios],
  );

  const toggleCollapsedValue = (
    value: string,
    setCollapsedValues: React.Dispatch<React.SetStateAction<Set<string>>>,
  ) => {
    setCollapsedValues((previous) => {
      const next = new Set(previous);
      if (next.has(value)) {
        next.delete(value);
      } else {
        next.add(value);
      }
      return next;
    });
  };

  const togglePortfolioBranchSelection = (node: PortfolioTreeNode) => {
    const branchPaths = collectPortfolioBranchPaths(node);

    setSelectedPortfolios((previousSelections) => {
      const selectionSet = new Set(previousSelections);
      const allBranchPathsSelected = branchPaths.every((path) =>
        selectionSet.has(path),
      );

      if (allBranchPathsSelected) {
        branchPaths.forEach((path) => selectionSet.delete(path));
      } else {
        branchPaths.forEach((path) => selectionSet.add(path));
      }

      return portfolios.filter((path) => selectionSet.has(path));
    });
  };

  const renderPortfolioNode = (node: PortfolioTreeNode, depth = 0): React.ReactNode => {
    const branchPaths = collectPortfolioBranchPaths(node);
    const selectedCount = branchPaths.filter((path) =>
      selectedPortfolioSet.has(path),
    ).length;
    const isChecked = selectedCount === branchPaths.length;
    const isIndeterminate = selectedCount > 0 && !isChecked;
    const isCollapsed = collapsedPortfolioNodes.has(node.path);
    const hasChildren = node.children.length > 0;

    return (
      <div key={node.path} className={depth > 0 ? 'ml-5 mt-1' : 'mt-1'}>
        <div className="flex items-center gap-2 text-sm text-gray-700">
          {hasChildren ? (
            <button
              type="button"
              aria-label={isCollapsed ? 'Expand portfolio level' : 'Collapse portfolio level'}
              onClick={() => toggleCollapsedValue(node.path, setCollapsedPortfolioNodes)}
              className="w-4 text-xs text-gray-500 hover:text-gray-700"
            >
              {isCollapsed ? '+' : '-'}
            </button>
          ) : (
            <span className="w-4" />
          )}
          <input
            type="checkbox"
            checked={isChecked}
            ref={(input) => {
              if (input) {
                input.indeterminate = isIndeterminate;
              }
            }}
            onChange={() => togglePortfolioBranchSelection(node)}
            className="rounded border-gray-300 text-[#336094] focus:ring-[#336094]"
          />
          <span>{node.name}</span>
        </div>
        {hasChildren && !isCollapsed && (
          <div className="ml-4 border-l border-gray-200 pl-2">
            {node.children.map((childNode) => renderPortfolioNode(childNode, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  const toggleCapabilitySelection = (
    capabilityName: string,
    relatedSubcategories: string[],
  ) => {
    const isSelected = selectedCapabilities.includes(capabilityName);

    setSelectedCapabilities((prev) =>
      isSelected
        ? prev.filter((currentValue) => currentValue !== capabilityName)
        : [...prev, capabilityName],
    );

    setSelectedSubcategories((prev) => {
      if (isSelected) {
        return prev.filter(
          (subcategory) => !relatedSubcategories.includes(subcategory),
        );
      }

      return Array.from(new Set([...prev, ...relatedSubcategories]));
    });
  };

  const toggleCapabilityLevelSelection = (
    level: CapabilityHierarchyLevel,
  ) => {
    const levelCapabilityNames = level.capabilities.map((capability) => capability.name);
    const levelSubcategoryNames = level.capabilities.flatMap((capability) =>
      capability.subcategories.map((subcategory) => subcategory.name),
    );

    const levelFullySelected = levelCapabilityNames.every((name) =>
      selectedCapabilities.includes(name),
    ) && levelSubcategoryNames.every((name) =>
      selectedSubcategories.includes(name),
    );

    setSelectedCapabilities((previous) => {
      const next = new Set(previous);
      if (levelFullySelected) {
        levelCapabilityNames.forEach((name) => next.delete(name));
      } else {
        levelCapabilityNames.forEach((name) => next.add(name));
      }
      return Array.from(next);
    });

    setSelectedSubcategories((previous) => {
      const next = new Set(previous);
      if (levelFullySelected) {
        levelSubcategoryNames.forEach((name) => next.delete(name));
      } else {
        levelSubcategoryNames.forEach((name) => next.add(name));
      }
      return Array.from(next);
    });
  };

  const getCapabilityNodeKey = (
    levelName: string,
    capabilityName: string,
  ) => `${levelName}::${capabilityName}`;

  const toggleSubcategorySelection = (
    capabilityName: string,
    subcategoryName: string,
  ) => {
    const isSelected = selectedSubcategories.includes(subcategoryName);

    setSelectedSubcategories((prev) =>
      isSelected
        ? prev.filter((currentValue) => currentValue !== subcategoryName)
        : [...prev, subcategoryName],
    );

    if (!isSelected && !selectedCapabilities.includes(capabilityName)) {
      setSelectedCapabilities((prev) => [...prev, capabilityName]);
    }
  };

  const handleApplyCapabilityFilters = () => {
    setAppliedPortfolios([...selectedPortfolios]);
    setAppliedCapabilities([...selectedCapabilities]);
    setAppliedSubcategories([...selectedSubcategories]);
    setCapabilityMappingPage(1);
    setCapabilityMappingError(null);
  };

  const getDeepestCapabilitySubcategory = (capabilityPath: CapabilityPath) => {
    const hierarchyValues = [capabilityPath.l5, capabilityPath.l4, capabilityPath.l3];

    return hierarchyValues.find(
      (value) => value && value !== CAPABILITY_PATH_PLACEHOLDER,
    ) ?? '';
  };

  const getPortfolioSegments = (portfolio: string) => portfolio
    .split(PORTFOLIO_SEGMENT_SEPARATOR)
    .map((segment) => segment.trim().toLowerCase())
    .filter(Boolean);

  const portfolioMatchesSelection = (
    selectedPortfolio: string,
    applicationPortfolio: string,
  ) => {
    const selectedSegments = getPortfolioSegments(selectedPortfolio);
    const applicationSegments = getPortfolioSegments(applicationPortfolio);

    if (
      selectedSegments.length === 0 ||
      applicationSegments.length === 0 ||
      selectedSegments.length > applicationSegments.length
    ) {
      return false;
    }

    return selectedSegments.every(
      (segment, index) => applicationSegments[index] === segment,
    );
  };

  const portfolioMatchesFilters = (
    applicationPortfolio: string,
    portfolioFilterInactive: boolean,
  ) => portfolioFilterInactive || appliedPortfolios.some((selectedPortfolio) =>
    portfolioMatchesSelection(selectedPortfolio, applicationPortfolio),
  );

  const displayedCapabilityMappings = capabilityMapping
    ? capabilityMapping.mappings
      .filter((mapping) => {
        const portfolioFilterInactive = appliedPortfolios.length === 0
          || appliedPortfolios.length === portfolios.length;
        const capabilityFilterInactive = appliedCapabilities.length === 0
          || appliedCapabilities.length === capabilities.length;
        const subcategoryFilterInactive = appliedSubcategories.length === 0
          || appliedSubcategories.length === subcategories.length;

        const capabilityMatches = capabilityFilterInactive
          || appliedCapabilities.includes(mapping.capability_path.l2);
        const subcategoryValue = getDeepestCapabilitySubcategory(
          mapping.capability_path,
        );
        const subcategoryMatches = subcategoryFilterInactive
          || appliedSubcategories.includes(subcategoryValue);

        if (!capabilityMatches || !subcategoryMatches) {
          return false;
        }

        if (mapping.matching_applications.length === 0) {
          return true;
        }

        return mapping.matching_applications.some(
          (app) => portfolioMatchesFilters(app.portfolio, portfolioFilterInactive),
        );
      })
      .map((mapping) => {
        const portfolioFilterInactive = appliedPortfolios.length === 0
          || appliedPortfolios.length === portfolios.length;
        const filteredApplications = portfolioFilterInactive
          ? mapping.matching_applications
          : mapping.matching_applications.filter((app) =>
            portfolioMatchesFilters(app.portfolio, portfolioFilterInactive),
          );

        return {
          ...mapping,
          matching_applications: filteredApplications,
          gap_identified: filteredApplications.length === 0,
        };
      })
    : [];

  /**
   * Directly skip an application without showing modal
   */
  const handleSkipApp = async (
    requirementTitle: string,
    requirementDescription: string | undefined,
    capabilityPath: { l1: string; l2: string; l3?: string; l4?: string; l5?: string },
    app: {
      app_number: string;
      name: string;
      portfolio: string;
      match_level: string;
      functional_description?: string;
    },
  ) => {
    if (!projectId) {return;}

    const key = `${requirementTitle}-${app.app_number}`;
    setSelectionSaving(key);

    try {
      await capabilityMappingService.addSelection(Number.parseInt(projectId, 10), {
        requirement_title: requirementTitle,
        requirement_description: requirementDescription,
        capability_path: capabilityPath,
        app_number: app.app_number,
        app_name: app.name,
        portfolio: app.portfolio,
        match_level: app.match_level,
        functional_description: app.functional_description,
        status: SELECTION_STATUS_SKIPPED,
        reason: '',
      });

      // Update sets
      const newSkipped = new Set(skippedApps).add(key);
      const newAccepted = new Set(acceptedApps);
      newAccepted.delete(key);

      setSkippedApps(newSkipped);
      setAcceptedApps(newAccepted);

      // Check if all apps have been reviewed and auto-export
      if (capabilityMapping?.mappings) {
        const totalApps = capabilityMapping.mappings.reduce(
          (count, m) => count + (m.matching_applications?.length || 0),
          0,
        );
        const reviewedCount = newAccepted.size + newSkipped.size;

        if (reviewedCount === totalApps && totalApps > 0) {
          setIsExportingSelections(true);
          try {
            const result = await capabilityMappingService.exportSelectionsAsMarkdown(
              Number.parseInt(projectId, 10),
            );
            setExportSuccess(`Saved to ${result.saved_file}.`);
            setTimeout(() => setExportSuccess(null), 5000);
          } catch (exportError) {
            logger.error(EXPORT_SELECTIONS_LOG_MESSAGE, exportError);
            setCapabilityMappingError(EXPORT_SELECTIONS_ERROR_MESSAGE);
          } finally {
            setIsExportingSelections(false);
          }
        }
      }
    } catch (error) {
      logger.error('Failed to skip application:', error);
    } finally {
      setSelectionSaving(null);
    }
  };

  /**
   * Submit the selection with reason
   */
  const handleSubmitReason = async () => {
    if (!projectId || !reasonModalData || !reasonText.trim()) {return;}

    const { requirementTitle, requirementDescription, capabilityPath, app } = reasonModalData;
    const key = `${requirementTitle}-${app.app_number}`;
    const status = reasonModalAction === ACTION_ACCEPT ? SELECTION_STATUS_ACCEPTED : SELECTION_STATUS_SKIPPED;

    setSelectionSaving(key);
    setReasonModalOpen(false);

    try {
      await capabilityMappingService.addSelection(Number.parseInt(projectId, 10), {
        requirement_title: requirementTitle,
        requirement_description: requirementDescription,
        capability_path: capabilityPath,
        app_number: app.app_number,
        app_name: app.name,
        portfolio: app.portfolio,
        match_level: app.match_level,
        functional_description: app.functional_description,
        status,
        reason: reasonText.trim(),
      });

      // Update the reasons map
      setSelectionReasons(prev => new Map(prev).set(key, reasonText.trim()));

      // Calculate new sets
      let newAccepted: Set<string>;
      let newSkipped: Set<string>;

      if (status === SELECTION_STATUS_ACCEPTED) {
        newAccepted = new Set(acceptedApps).add(key);
        newSkipped = new Set(skippedApps);
        newSkipped.delete(key);
      } else {
        newSkipped = new Set(skippedApps).add(key);
        newAccepted = new Set(acceptedApps);
        newAccepted.delete(key);
      }

      setAcceptedApps(newAccepted);
      setSkippedApps(newSkipped);

      // Check if all apps have been reviewed and auto-export
      if (capabilityMapping?.mappings) {
        const totalApps = capabilityMapping.mappings.reduce(
          (count, m) => count + (m.matching_applications?.length || 0),
          0,
        );
        const reviewedCount = newAccepted.size + newSkipped.size;

        if (reviewedCount === totalApps && totalApps > 0) {
          // All apps reviewed - auto export
          setIsExportingSelections(true);
          try {
            const result = await capabilityMappingService.exportSelectionsAsMarkdown(
              Number.parseInt(projectId, 10),
            );
            setExportSuccess(`Saved to ${result.saved_file}.`);
            setTimeout(() => setExportSuccess(null), 5000);
          } catch (exportError) {
            logger.error(EXPORT_SELECTIONS_LOG_MESSAGE, exportError);
            setCapabilityMappingError(EXPORT_SELECTIONS_ERROR_MESSAGE);
          } finally {
            setIsExportingSelections(false);
          }
        }
      }
    } catch (error) {
      logger.error(`Failed to ${reasonModalAction} application:`, error);
    } finally {
      setSelectionSaving(null);
      setReasonModalData(null);
      setReasonText('');
    }
  };

  /**
   * Manually export current selections to markdown file
   */
  const handleManualExportSelections = async () => {
    if (!projectId) {return;}

    const hasSelections = acceptedApps.size > 0 || skippedApps.size > 0;
    if (!hasSelections) {
      setCapabilityMappingError('No selections to save. Please accept or skip at least one application.');
      setTimeout(() => setCapabilityMappingError(null), 3000);
      return;
    }

    setIsExportingSelections(true);
    try {
      const result = await capabilityMappingService.exportSelectionsAsMarkdown(
        Number.parseInt(projectId, 10),
      );
      setExportSuccess(`Saved to ${result.saved_file}.`);
      setTimeout(() => setExportSuccess(null), 5000);
      void refreshContextSignature();
    } catch (exportError) {
      logger.error(EXPORT_SELECTIONS_LOG_MESSAGE, exportError);
      setCapabilityMappingError(EXPORT_SELECTIONS_ERROR_MESSAGE);
    } finally {
      setIsExportingSelections(false);
    }
  };

  /**
   * Handle going back to context management
   */
  const handleBackToContext = () => {
    if (projectId) {
      navigate(`/project/${projectId}/context`);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#336094] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading project analysis...</p>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Unable to Load Analysis</h2>
          <p className="text-gray-600 mb-4">{error || 'Project not found'}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-[#336094] text-white px-4 py-2 rounded-md hover:bg-[#2a4d7a] transition-colors main-button"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Stage Navigation */}
      <StageNavigationBar
        currentStage={3}
        projectId={projectId ? Number.parseInt(projectId, 10) : 0}
        isBrownfield={project?.projectType === ProjectType.BROWN_FIELD}
        projectStatus={(() => {
          const statusMap: Record<string, ProjectStatus> = {
            'context_upload': ProjectStatus.CONTEXT_UPLOAD,
            'context_review': ProjectStatus.CONTEXT_REVIEW,
            'adr': ProjectStatus.ADR,
            'generation': ProjectStatus.GENERATION,
            'bizzdesign_integration': ProjectStatus.BIZZDESIGN_INTEGRATION,
            'completed': ProjectStatus.COMPLETED,
          };
          return statusMap[project.status] ?? ProjectStatus.CONTEXT_UPLOAD;
        })()}
        onStageClick={handleStageClick}
      />

      <div className={`${STAGE_PAGE_WIDTH_CLASS} py-8`}>
        {/* Header */}
        <PageHeader
          title="Context Review & Analysis"
          subtitle={`Analyze and validate project context documents for ${project.title}`}
          icon={DocumentMagnifyingGlassIcon}
          iconBgColor="#336094"
        />

        {/* Analysis Results */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
              <ChartBarIcon className="w-5 h-5" />
              Analysis Results
            </h2>
            <div className="flex items-center gap-2">
              {analysisResults.length > 0 && (
                <button
                  onClick={() => setIsAnalysisDetailsOpen(!isAnalysisDetailsOpen)}
                  className="border border-gray-300 bg-white px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:border-[#336094]"
                  aria-expanded={isAnalysisDetailsOpen}
                >
                  {isAnalysisDetailsOpen ? 'Hide details' : 'View details'}
                </button>
              )}
              {!isAnalyzing && analysisResults.length > 0 && (
                <button
                  onClick={handleRegenerateAnalysis}
                  className="w-40 justify-center bg-[#336094] text-white px-4 py-2 rounded-md hover:bg-[#2a4d7a] transition-colors text-sm main-button"
                >
                  Run Analysis
                </button>
              )}
            </div>
          </div>
          {isAnalysisStale && !isAnalyzing && (
            <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4">
              <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
              <div>
                <p className="text-sm font-semibold text-amber-900">
                  New User Input — Re-Run Analysis
                </p>
                <p className="text-sm text-amber-800">
                  The project context has changed since the last analysis. Re-run the
                  analysis so the score reflects the latest input.
                </p>
              </div>
            </div>
          )}
          {isAnalyzing ? renderAnalysisResults() : analysisResults.length > 0 && (() => {
            const projectSummary = getProjectAnalysisSummary();
            return (
              <div className="flex flex-wrap items-center gap-5 rounded-lg border border-gray-200 bg-white p-5">
                <div className="flex h-20 w-20 flex-col items-center justify-center rounded-md bg-gray-50">
                  <span className={`text-2xl font-bold ${getScoreColor(projectSummary.score)}`}>
                    {projectSummary.score}
                  </span>
                  <span className="text-xs text-gray-500">/100</span>
                </div>
                <div className="max-w-3xl space-y-2 text-sm leading-relaxed text-gray-600 break-words">
                  <p>{renderTextWithLinks(projectSummary.description)}</p>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">
                      Archie&apos;s Recommendation:
                    </h3>
                    <p className="mt-1">
                      {renderTextWithLinks(projectSummary.recommendation)}
                    </p>
                  </div>
                </div>
              </div>
            );
          })()}
          {isAnalysisDetailsOpen && !isAnalyzing && (
            <div className="space-y-3 rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">View:</span>
                <div className="flex bg-gray-100 rounded-md p-1">
                  <button
                    onClick={() => setViewMode('cards')}
                    className={`flex items-center gap-1 px-3 py-1 rounded text-sm font-medium ${viewMode === 'cards' ? 'bg-white text-[#336094] shadow-sm' : 'text-gray-600'}`}
                  >
                    <Squares2X2Icon className="w-4 h-4" />
                    Cards
                  </button>
                  <button
                    onClick={() => setViewMode('table')}
                    className={`flex items-center gap-1 px-3 py-1 rounded text-sm font-medium ${viewMode === 'table' ? 'bg-white text-[#336094] shadow-sm' : 'text-gray-600'}`}
                  >
                    <TableCellsIcon className="w-4 h-4" />
                    Table
                  </button>
                </div>
              </div>
              {renderAnalysisResults()}
            </div>
          )}
        </div>

        {/* Project-level Capability Mapping */}
        <div className="space-y-4 mt-8">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <CubeIcon className="w-5 h-5" />
                Capability Mapping
              </h2>
              <p className="mt-1 text-sm text-gray-600">
                Identify the EBM capability that best fits the complete solution context.
              </p>
            </div>
            <button
              onClick={handleGenerateCapabilityRecommendation}
              disabled={isGeneratingRecommendation}
              className="w-40 justify-center bg-[#336094] text-white px-4 py-2 rounded-md hover:bg-[#2a4d7a] transition-colors text-sm main-button disabled:opacity-50"
            >
              Map Capability
            </button>
          </div>

          {recommendationError && (
            <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {recommendationError}
            </p>
          )}

          {isGeneratingRecommendation && (
            <div className="rounded-lg border border-gray-200 bg-white p-6">
              <div className="flex items-center gap-4">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-[#336094]" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-900">
                    Archie is mapping your solution to an EBM capability
                  </p>
                  <p className="mt-1 text-sm text-gray-600">
                    Reading project context and checking the capability catalogue.
                  </p>
                  <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-gray-100">
                    <div className="h-full w-2/3 animate-pulse rounded-full bg-[#336094]" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {!capabilityRecommendation && !isGeneratingRecommendation && (
            <div className="rounded-lg border border-gray-200 bg-white p-6">
              <div className="py-8 text-center">
                <CubeIcon className="mx-auto mb-4 h-12 w-12 text-gray-400" />
                <h3 className="text-lg font-semibold text-gray-900">
                  No Capability Mapping Yet
                </h3>
                <p className="mt-2 text-gray-500">
                  Click Map Capability to identify the EBM capability that best fits your
                  solution context.
                </p>
              </div>
            </div>
          )}

          {capabilityRecommendation && (
            <div className="rounded-lg border border-gray-200 bg-white p-5">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-medium uppercase text-gray-500">Recommended EBM capability</p>
                  <h3 className="mt-1 text-lg font-semibold text-gray-900">
                    {[
                      capabilityRecommendation.recommendation.capability_path.l1,
                      capabilityRecommendation.recommendation.capability_path.l2,
                      capabilityRecommendation.recommendation.capability_path.l3,
                      capabilityRecommendation.recommendation.capability_path.l4,
                      capabilityRecommendation.recommendation.capability_path.l5,
                    ].filter((value) => value && value !== 'N/A').join(' > ')}
                  </h3>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleOpenCapabilityChange}
                    className="border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 rounded-md hover:border-[#336094]"
                    aria-expanded={isOverrideOpen}
                  >
                    Change
                  </button>
                </div>
              </div>
              <div className="mt-4 border-t border-gray-200 pt-4">
                <h4 className="text-sm font-semibold text-gray-900">Description</h4>
                <p className="mt-1 text-sm text-gray-700">
                  {capabilityRecommendation.recommendation.capability_definition}
                </p>
              </div>
              {isOverrideOpen && (
                <div className="mt-4 border-t border-gray-200 pt-4">
                  <label className="block text-sm font-medium text-gray-700" htmlFor="capability-override-search">
                    Choose an EBM capability
                  </label>
                  <input
                    id="capability-override-search"
                    value={overrideSearch}
                    onChange={(event) => setOverrideSearch(event.target.value)}
                    placeholder="Search capability hierarchy"
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-[#336094] focus:outline-none focus:ring-1 focus:ring-[#336094]"
                  />
                  <div className="mt-2 max-h-44 overflow-y-auto rounded-md border border-gray-200">
                    {capabilityOverrideOptions.filter((path) =>
                      Object.values(path).filter(Boolean).join(' ').toLowerCase().includes(overrideSearch.toLowerCase()),
                    ).map((path) => {
                      const label = Object.values(path).filter(Boolean).join(' > ');
                      return (
                        <button
                          key={label}
                          type="button"
                          onClick={() => handleCapabilitySelection(path)}
                          className={`block w-full px-3 py-2 text-left text-sm hover:bg-gray-50 ${overridePath === path ? 'bg-[#fff7b4]' : ''}`}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                  {overridePath && (
                    <p className="mt-3 text-sm text-gray-700">
                      Selected: {Object.values(overridePath).filter(Boolean).join(' > ')}
                    </p>
                  )}
                  {isLoadingSelectedCapabilityDefinition && (
                    <p className="mt-3 text-sm text-gray-500">
                      Loading official EBM description...
                    </p>
                  )}
                  {overrideDefinition && (
                    <p className="mt-1 text-sm text-gray-600">{overrideDefinition}</p>
                  )}
                  <textarea
                    value={overrideRationale}
                    onChange={(event) => setOverrideRationale(event.target.value)}
                    placeholder="Optional rationale for this change"
                    rows={2}
                    className="mt-3 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-[#336094] focus:outline-none focus:ring-1 focus:ring-[#336094]"
                  />
                  <button
                    onClick={() => overridePath && handleConfirmCapabilityRecommendation(overridePath)}
                    disabled={!overridePath || isConfirmingRecommendation
                      || isLoadingSelectedCapabilityDefinition}
                    className="mt-3 bg-[#336094] px-3 py-2 text-sm font-medium text-white rounded-md disabled:opacity-50"
                  >
                    Change Capability
                  </button>
                </div>
              )}
              {!isOverrideOpen && (
                <div className="mt-4 space-y-4 border-t border-gray-200 pt-4">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900">Reasoning</h4>
                    <p className="mt-1 text-sm text-gray-600">
                      {capabilityRecommendation.recommendation.rationale}
                    </p>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900">Source</h4>
                    {capabilityRecommendation.recommendation.evidence.length > 0 ? (
                      <div className="mt-2 space-y-2">
                        {groupRecommendationSources(
                          capabilityRecommendation.recommendation.evidence,
                        ).map((source) => {
                          const sourceKey = source.documentTitle;
                          const isExpanded = expandedRecommendationSources.has(sourceKey);

                          return (
                            <div key={sourceKey} className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex items-start gap-2">
                                  <BookmarkSquareIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-500" />
                                  <p className="text-sm font-medium text-gray-700">
                                    {source.documentTitle}
                                  </p>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => setExpandedRecommendationSources((previous) => {
                                    const next = new Set(previous);
                                    if (next.has(sourceKey)) {
                                      next.delete(sourceKey);
                                    } else {
                                      next.add(sourceKey);
                                    }
                                    return next;
                                  })}
                                  className="text-sm font-medium text-[#336094] underline underline-offset-2 hover:text-[#6e94c0]"
                                  aria-expanded={isExpanded}
                                >
                                  {isExpanded ? 'Hide' : 'View'}
                                </button>
                              </div>
                              {isExpanded && (
                                <ul className="mt-2 list-disc space-y-1 pl-5 text-sm leading-relaxed text-gray-600">
                                  {source.details.map((detail) => (
                                    <li key={detail}>{detail}</li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="mt-1 text-sm text-gray-600">
                      No attributable context source was returned.
                      </p>
                    )}
                  </div>
                </div>
              )}
              {confirmedRecommendationFile && (
                <p className="mt-3 text-sm font-medium text-[#008557]">
                  Confirmed and saved to {confirmedRecommendationFile}.
                </p>
              )}

              <div className="mt-5 border-t border-gray-200 pt-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <h4 className="text-sm font-semibold text-gray-900">Application List</h4>
                  <button
                    type="button"
                    onClick={saveCapabilityApplications}
                    disabled={isSavingCapabilityApplications}
                    className={[
                      'inline-flex items-center gap-2 rounded-lg px-4 py-2',
                      'font-medium transition-colors duration-200',
                      isSavingCapabilityApplications
                        ? 'cursor-not-allowed bg-gray-100 text-gray-400'
                        : 'bg-[#336094] text-white hover:bg-[#2a4f7a]',
                    ].join(' ')}
                  >
                    {isSavingCapabilityApplications ? (
                      <>
                        <span className="animate-spin">⏳</span>
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>
                        <DocumentTextIcon className="h-4 w-4" />
                        <span>Save Selection</span>
                      </>
                    )}
                  </button>
                </div>
                <input
                  value={applicationListSearch}
                  onChange={(event) => setApplicationListSearch(event.target.value)}
                  placeholder="Search applications"
                  className="mt-3 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-[#336094] focus:outline-none focus:ring-1 focus:ring-[#336094]"
                />
                {isLoadingCapabilityApplications ? (
                  <p className="mt-3 text-sm text-gray-600">Loading applications...</p>
                ) : (
                  <div className="mt-3 max-h-64 overflow-y-auto rounded-md border border-gray-200">
                    {capabilityApplications.filter((application) => {
                      const searchValue = applicationListSearch.toLowerCase();
                      return [application.name, application.app_number, application.portfolio]
                        .join(' ')
                        .toLowerCase()
                        .includes(searchValue);
                    }).map((application) => {
                      const applicationKey =
                        application.app_number || application.name;
                      const isSelected = selectedCapabilityApplications.some(
                        (item) => item.app_number === application.app_number,
                      );
                      const description =
                        application.functional_description ?? '';
                      const hasDescription =
                        description !== '' && description !== 'N/A';
                      const isDescriptionExpanded =
                        expandedApplicationDescriptions.has(applicationKey);
                      const needsTruncation =
                        description.length > APPLICATION_DESCRIPTION_PREVIEW_LENGTH;
                      const shownDescription =
                        !needsTruncation || isDescriptionExpanded
                          ? description
                          : `${description
                            .slice(0, APPLICATION_DESCRIPTION_PREVIEW_LENGTH)
                            .trimEnd()}\u2026`;
                      return (
                        <label
                          key={applicationKey}
                          className="flex cursor-pointer items-start gap-3 border-b border-gray-100 px-3 py-3 last:border-b-0 hover:bg-gray-50"
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleCapabilityApplication(application)}
                            aria-label={`Select ${application.name}`}
                            className="mt-1 rounded border-gray-300 text-[#336094] focus:ring-[#336094]"
                          />
                          <span>
                            <span className="block text-sm font-medium text-gray-900">
                              {application.name}
                            </span>
                            <span className="block text-xs text-gray-500">
                              {application.app_number} {application.portfolio && `- ${application.portfolio}`}
                            </span>
                            {hasDescription && (
                              <span className="mt-1 block text-xs leading-relaxed text-gray-600">
                                {shownDescription}
                                {needsTruncation && (
                                  <button
                                    type="button"
                                    onClick={(event) => {
                                      event.preventDefault();
                                      event.stopPropagation();
                                      setExpandedApplicationDescriptions((previous) => {
                                        const next = new Set(previous);
                                        if (next.has(applicationKey)) {
                                          next.delete(applicationKey);
                                        } else {
                                          next.add(applicationKey);
                                        }
                                        return next;
                                      });
                                    }}
                                    className="ml-1 font-medium text-[#336094] underline underline-offset-2 hover:text-[#2a4d7a]"
                                  >
                                    {isDescriptionExpanded ? 'Show less' : 'Show more'}
                                  </button>
                                )}
                              </span>
                            )}
                          </span>
                        </label>
                      );
                    })}
                    {!capabilityApplications.some((application) => {
                      const searchValue = applicationListSearch.toLowerCase();
                      return [application.name, application.app_number, application.portfolio]
                        .join(' ')
                        .toLowerCase()
                        .includes(searchValue);
                    }) && (
                      <p className="px-3 py-4 text-sm text-gray-600">
                        No applications match this capability or search.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Application Finder Section */}
        <div className="space-y-6 mt-8">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <CubeIcon className="w-5 h-5" />
                Application Finder
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Search for other applications in Shell that are matching your
                business requirements
              </p>
            </div>
            <button
              onClick={handleGenerateCapabilityMapping}
              disabled={isGeneratingCapabilityMapping}
              className="flex w-40 justify-center gap-2 whitespace-nowrap rounded-md bg-[#336094] px-4 py-2 text-sm text-white transition-colors hover:bg-[#2a4d7a] main-button disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isGeneratingCapabilityMapping ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white" />
                  <span>Search Apps</span>
                </>
              ) : (
                <span>Search Apps</span>
              )}
            </button>
          </div>

          {/* Portfolio Selector Row */}
          <div className="flex flex-wrap items-end gap-4">
            {/* Portfolio Multi-Select Dropdown */}
            {portfolios.length > 0 && (
              <div className="flex-1 min-w-[220px] max-w-sm" ref={portfolioDropdownRef}>
                <span className="block text-sm font-medium text-gray-700 mb-1">
                  Filter by Portfolio
                </span>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setIsPortfolioDropdownOpen(!isPortfolioDropdownOpen)}
                    className="w-full bg-white border border-gray-300 rounded-md px-3 py-2 text-left text-sm flex items-center justify-between hover:border-[#336094] focus:outline-none focus:ring-2 focus:ring-[#336094] focus:border-[#336094] transition-colors"
                  >
                    <span className="truncate text-gray-700">
                      {getPortfolioTreeSummary()}
                    </span>
                    <svg
                      className={`w-4 h-4 text-gray-400 transition-transform ${isPortfolioDropdownOpen ? 'rotate-180' : ''}`}
                      fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {isPortfolioDropdownOpen && (
                    <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
                      <div className="sticky top-0 bg-white border-b border-gray-100 px-3 py-2 flex items-center justify-between">
                        <button
                          type="button"
                          onClick={() => setSelectedPortfolios([...portfolios])}
                          className="text-xs text-[#336094] hover:underline font-medium"
                        >
                          Select All
                        </button>
                        <button
                          type="button"
                          onClick={() => setSelectedPortfolios([])}
                          className="text-xs text-gray-500 hover:underline"
                        >
                          Deselect All
                        </button>
                      </div>
                      <div className="px-3 py-2">
                        {portfolioTree.map((node) => renderPortfolioNode(node))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {capabilities.length > 0 && (
              <div className="flex-1 min-w-[220px] max-w-sm" ref={capabilityDropdownRef}>
                <span className="block text-sm font-medium text-gray-700 mb-1">
                  Filter by Capability Tree
                </span>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setIsCapabilityDropdownOpen(!isCapabilityDropdownOpen)}
                    className="w-full bg-white border border-gray-300 rounded-md px-3 py-2 text-left text-sm flex items-center justify-between hover:border-[#336094] focus:outline-none focus:ring-2 focus:ring-[#336094] focus:border-[#336094] transition-colors"
                  >
                    <span className="truncate text-gray-700">
                      {getCapabilityTreeSummary()}
                    </span>
                    <svg
                      className={`w-4 h-4 text-gray-400 transition-transform ${isCapabilityDropdownOpen ? 'rotate-180' : ''}`}
                      fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {isCapabilityDropdownOpen && (
                    <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
                      <div className="sticky top-0 bg-white border-b border-gray-100 px-3 py-2 flex items-center justify-between">
                        <button
                          type="button"
                          onClick={() => setSelectedCapabilities([...capabilities])}
                          className="text-xs text-[#336094] hover:underline font-medium"
                        >
                          Select All
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedCapabilities([]);
                            setSelectedSubcategories([]);
                          }}
                          className="text-xs text-gray-500 hover:underline"
                        >
                          Clear Filter
                        </button>
                      </div>
                      {capabilityHierarchy.map((level) => (
                        <div key={level.name} className="border-t border-gray-100 first:border-t-0">
                          <div className="px-3 py-2 bg-gray-50 flex items-center gap-2">
                            <button
                              type="button"
                              aria-label={collapsedCapabilityLevels.has(level.name)
                                ? 'Expand capability level'
                                : 'Collapse capability level'}
                              onClick={() => toggleCollapsedValue(level.name, setCollapsedCapabilityLevels)}
                              className="w-4 text-xs text-gray-500 hover:text-gray-700"
                            >
                              {collapsedCapabilityLevels.has(level.name) ? '+' : '-'}
                            </button>
                            <input
                              type="checkbox"
                              checked={level.capabilities.every((capability) =>
                                selectedCapabilities.includes(capability.name),
                              ) && level.capabilities.flatMap((capability) =>
                                capability.subcategories.map((subcategory) =>
                                  subcategory.name,
                                ),
                              ).every((subcategoryName) =>
                                selectedSubcategories.includes(subcategoryName),
                              )}
                              ref={(input) => {
                                if (!input) {
                                  return;
                                }
                                const levelCapabilityNames = level.capabilities.map(
                                  (capability) => capability.name,
                                );
                                const levelSubcategoryNames = level.capabilities.flatMap(
                                  (capability) => capability.subcategories.map(
                                    (subcategory) => subcategory.name,
                                  ),
                                );
                                const selectedCount = levelCapabilityNames.filter((name) =>
                                  selectedCapabilities.includes(name),
                                ).length + levelSubcategoryNames.filter((name) =>
                                  selectedSubcategories.includes(name),
                                ).length;
                                const totalCount = (
                                  levelCapabilityNames.length + levelSubcategoryNames.length
                                );
                                input.indeterminate = selectedCount > 0
                                  && selectedCount < totalCount;
                              }}
                              onChange={() => toggleCapabilityLevelSelection(level)}
                              className="rounded border-gray-300 text-[#336094] focus:ring-[#336094]"
                            />
                            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                              {level.name}
                            </span>
                          </div>
                          {!collapsedCapabilityLevels.has(level.name)
                            && level.capabilities.map((capability) => {
                              const capabilityNodeKey = getCapabilityNodeKey(
                                level.name,
                                capability.name,
                              );
                              const capabilityCollapsed = collapsedCapabilityNodes.has(
                                capabilityNodeKey,
                              );

                              return (
                                <div key={capability.name} className="px-3 py-2">
                                  <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                                    {capability.subcategories.length > 0 ? (
                                      <button
                                        type="button"
                                        aria-label={capabilityCollapsed
                                          ? 'Expand capability node'
                                          : 'Collapse capability node'}
                                        onClick={() => toggleCollapsedValue(
                                          capabilityNodeKey,
                                          setCollapsedCapabilityNodes,
                                        )}
                                        className="w-4 text-xs text-gray-500 hover:text-gray-700"
                                      >
                                        {capabilityCollapsed ? '+' : '-'}
                                      </button>
                                    ) : (
                                      <span className="w-4" />
                                    )}
                                    <input
                                      type="checkbox"
                                      checked={selectedCapabilities.includes(capability.name)}
                                      onChange={() => toggleCapabilitySelection(
                                        capability.name,
                                        capability.subcategories.map((subcategory) =>
                                          subcategory.name,
                                        ),
                                      )}
                                      className="rounded border-gray-300 text-[#336094] focus:ring-[#336094]"
                                    />
                                    <span>{capability.name}</span>
                                  </div>
                                  {!capabilityCollapsed && capability.subcategories.length > 0 && (
                                    <div className="mt-2 ml-6 space-y-2 border-l border-gray-200 pl-3">
                                      {capability.subcategories.map((subcategory) => (
                                        <label
                                          key={`${capability.name}-${subcategory.name}`}
                                          className="flex items-center gap-2 cursor-pointer text-sm text-gray-600"
                                        >
                                          <input
                                            type="checkbox"
                                            checked={selectedSubcategories.includes(subcategory.name)}
                                            onChange={() => toggleSubcategorySelection(
                                              capability.name,
                                              subcategory.name,
                                            )}
                                            className="rounded border-gray-300 text-[#336094] focus:ring-[#336094]"
                                          />
                                          <span>{subcategory.name}</span>
                                        </label>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {capabilityMapping && (
              <button
                onClick={handleApplyCapabilityFilters}
                className="border border-[#336094] text-[#336094] px-4 py-2 rounded-md hover:bg-[#fff7b4] transition-colors text-sm whitespace-nowrap"
              >
                Apply Filter
              </button>
            )}

          </div>

          {/* Export Success Message */}
          {exportSuccess && (
            <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-start gap-2">
              <span className="text-lg">✓</span>
              <span>{exportSuccess}</span>
            </div>
          )}

          {/* Capability Mapping Error */}
          {capabilityMappingError && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-start gap-2">
              <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <span>{capabilityMappingError}</span>
            </div>
          )}

          {/* Capability Mapping Loading State */}
          {isGeneratingCapabilityMapping && (
            <div className="rounded-lg border border-gray-200 bg-white p-6">
              <div className="flex items-center gap-4">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-[#336094]" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-900">
                    Archie is searching matching applications
                  </p>
                  <p className="mt-1 text-sm text-gray-600">
                    Analyzing your requirements against the IT4IT application catalogue.
                  </p>
                  <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-gray-100">
                    <div className="h-full w-2/3 animate-pulse rounded-full bg-[#336094]" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Capability Mapping Results */}
          {capabilityMapping && !isGeneratingCapabilityMapping && (
            <div className="space-y-6">
              {displayedCapabilityMappings.length > 0 ? (
                <div className="max-h-[70vh] overflow-y-auto pr-2 space-y-6">
                  {displayedCapabilityMappings
                    .slice(
                      (capabilityMappingPage - 1) * MAPPINGS_PER_PAGE,
                      capabilityMappingPage * MAPPINGS_PER_PAGE,
                    )
                    .map((mapping, reqIndex) => (
                      <div key={reqIndex} className="space-y-4">
                        {/* Requirement Header */}
                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">
                            Business Requirement{' '}
                            {(capabilityMappingPage - 1) * MAPPINGS_PER_PAGE
                              + reqIndex + 1}
                            : {mapping.requirement}
                          </h3>
                          {mapping.capability_definition && (
                            <p className="text-sm text-gray-600 mb-3">
                              {mapping.capability_definition}
                            </p>
                          )}
                        </div>

                        {/* Application Cards */}
                        {mapping.matching_applications.length > 0 ? (
                          <div className="grid gap-4">
                            {mapping.matching_applications
                              .slice(0, MAX_APPS_PER_REQUIREMENT)
                              .map((app, appIndex) => (
                                <div
                                  key={appIndex}
                                  className="bg-white rounded-lg border border-gray-200 p-6 hover:border-[#336094] transition-colors"
                                >
                                  <div className="flex items-start justify-between gap-4">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-3 mb-3">
                                        <CubeIcon className="w-6 h-6 text-[#336094] flex-shrink-0" />
                                        <div>
                                          <h4 className="text-lg font-semibold text-gray-900">
                                            {app.name}
                                          </h4>
                                          <p className="text-sm text-gray-500">
                                            {app.app_number} • {app.portfolio}
                                          </p>
                                        </div>
                                      </div>
                                
                                      {app.functional_description && app.functional_description !== 'N/A' && (
                                        <p className="text-sm text-gray-600 mb-3">
                                          {app.functional_description}
                                        </p>
                                      )}
                                    </div>
                              
                                    <div className="flex gap-2">
                                      {(() => {
                                        const selectionKey = `${mapping.requirement}-${app.app_number}`;
                                        const isAccepted = acceptedApps.has(selectionKey);
                                        const isSkipped = skippedApps.has(selectionKey);
                                        const isSaving = selectionSaving === selectionKey;
                                        const reason = selectionReasons.get(selectionKey);
                                        // Same app already accepted under a different requirement
                                        const appSuffix = `-${app.app_number}`;
                                        const isAddedElsewhere = !isAccepted
                                          && Array.from(acceptedApps).some(
                                            (key) => key.endsWith(appSuffix),
                                          );

                                        if (isAccepted) {
                                          return (
                                            <div className="flex flex-col items-end gap-1">
                                              <div
                                                className="px-4 py-2 bg-green-100 text-green-800 rounded-md
                                            text-sm flex items-center gap-2 font-medium"
                                              >
                                                <span>✓</span>
                                                <span>Added</span>
                                              </div>
                                              {reason && (
                                                <p
                                                  className="text-xs text-gray-500 max-w-[200px]
                                              text-right italic"
                                                  title={reason}
                                                >
                                            &quot;{reason.length > 50
                                                    ? `${reason.substring(0, 50)}...`
                                                    : reason}&quot;
                                                </p>
                                              )}
                                            </div>
                                          );
                                        }

                                        if (isSkipped) {
                                          return (
                                            <div className="flex flex-col items-end gap-1">
                                              <div
                                                className="px-4 py-2 bg-gray-100 text-gray-500 rounded-md
                                            text-sm flex items-center gap-2"
                                              >
                                                <span>✕</span>
                                                <span>Skipped</span>
                                              </div>
                                              {reason && (
                                                <p
                                                  className="text-xs text-gray-500 max-w-[200px]
                                              text-right italic"
                                                  title={reason}
                                                >
                                            &quot;{reason.length > 50
                                                    ? `${reason.substring(0, 50)}...`
                                                    : reason}&quot;
                                                </p>
                                              )}
                                            </div>
                                          );
                                        }

                                        if (isAddedElsewhere) {
                                          return (
                                            <div className="flex flex-col items-end gap-1">
                                              <div
                                                className="px-4 py-2 bg-gray-100 text-gray-400
                                            rounded-md text-sm flex items-center gap-2
                                            font-medium cursor-not-allowed"
                                              >
                                                <span>✓</span>
                                                <span>Already added</span>
                                              </div>
                                              <p className="text-xs text-gray-400 max-w-[200px]
                                            text-right italic">
                                                Added under another requirement
                                              </p>
                                            </div>
                                          );
                                        }

                                        return (
                                          <>
                                            <button
                                              onClick={() => handleSkipApp(
                                                mapping.requirement,
                                                mapping.capability_definition,
                                                mapping.capability_path,
                                                app,
                                              )}
                                              disabled={isSaving}
                                              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors text-sm flex items-center gap-2 disabled:opacity-50"
                                            >
                                              <span>✕</span>
                                              <span>Skip</span>
                                            </button>
                                            <button
                                              onClick={() => handleAcceptApp(
                                                mapping.requirement,
                                                mapping.capability_definition,
                                                mapping.capability_path,
                                                app,
                                              )}
                                              disabled={isSaving}
                                              className="px-4 py-2 bg-[#FFD700] text-gray-900 rounded-md hover:bg-[#FFC700] transition-colors text-sm flex items-center gap-2 font-medium disabled:opacity-50"
                                            >
                                              {isSaving ? (
                                                <>
                                                  <span className="animate-spin">⏳</span>
                                                  <span>Saving...</span>
                                                </>
                                              ) : (
                                                <>
                                                  <span>✓</span>
                                                  <span>Accept & Add</span>
                                                </>
                                              )}
                                            </button>
                                          </>
                                        );
                                      })()}
                                    </div>
                                  </div>
                                </div>
                              ))}
                          </div>
                        ) : (
                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                            <p className="text-sm text-yellow-800">
                              <strong>Gap Identified:</strong> No applications found for this
                          capability in the IT4IT database.
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              ) : (
                <div className="bg-white rounded-lg p-6 border border-gray-200">
                  <div className="py-8 text-center">
                    <h3 className="text-lg font-semibold text-gray-900">
                      No Application Mapping Yet
                    </h3>
                    <p className="mt-2 text-gray-500">
                      Click Search Apps to find applications for your requirements.
                    </p>
                  </div>
                </div>
              )}

              {/* Pagination Controls */}
              {displayedCapabilityMappings.length > MAPPINGS_PER_PAGE && (() => {
                const totalPages = Math.ceil(
                  displayedCapabilityMappings.length / MAPPINGS_PER_PAGE,
                );
                const startItem = (capabilityMappingPage - 1) * MAPPINGS_PER_PAGE + 1;
                return (
                  <div className="flex items-center justify-between border-t border-gray-200 pt-4">
                    <span className="text-sm text-gray-500">
                        Showing {startItem} of{' '}
                      {displayedCapabilityMappings.length} requirements
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setCapabilityMappingPage((p) => Math.max(1, p - 1))}
                        disabled={capabilityMappingPage === 1}
                        className="px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                          Previous
                      </button>
                      <span className="text-sm text-gray-700">
                          Page {capabilityMappingPage} of {totalPages}
                      </span>
                      <button
                        type="button"
                        onClick={() => setCapabilityMappingPage(
                          (p) => Math.min(totalPages, p + 1),
                        )}
                        disabled={capabilityMappingPage === totalPages}
                        className="px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                          Next
                      </button>
                    </div>
                  </div>
                );
              })()}

              {/* Save Selections Button */}
              {displayedCapabilityMappings.length > 0 && (
                <div className="mt-6 flex justify-end">
                  <button
                    onClick={handleManualExportSelections}
                    disabled={isExportingSelections || (acceptedApps.size === 0 && skippedApps.size === 0)}
                    className={`
                      inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium
                      transition-colors duration-200
                      ${isExportingSelections || (acceptedApps.size === 0 && skippedApps.size === 0)
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-[#336094] text-white hover:bg-[#2a4f7a]'
                }
                    `}
                  >
                    {isExportingSelections ? (
                      <>
                        <span className="animate-spin">⏳</span>
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>
                        <DocumentTextIcon className="w-4 h-4" />
                        <span>Save Selection</span>
                      </>
                    )}
                  </button>
                </div>
              )}
              
              {/* Quality Assessment & Execution Plan */}
              <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
                {/* Quality Assessment Bar */}
                <div className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <span className="text-sm font-semibold text-gray-900">
                      Quality Assessment
                    </span>
                    {(() => {
                      const total = displayedCapabilityMappings.length;
                      const withApps = displayedCapabilityMappings.filter(
                        (m) => m.matching_applications.length > 0,
                      ).length;
                      const coveragePercent = total > 0
                        ? Math.round((withApps / total) * 100)
                        : 0;
                      const barColor = coveragePercent >= GOOD_SCORE_THRESHOLD
                        ? 'bg-green-500'
                        : coveragePercent >= MEDIUM_SCORE_THRESHOLD
                          ? 'bg-yellow-500'
                          : 'bg-red-500';
                      const confidenceLabel = coveragePercent >= GOOD_SCORE_THRESHOLD
                        ? 'high confidence'
                        : coveragePercent >= MEDIUM_SCORE_THRESHOLD
                          ? 'medium confidence'
                          : 'low confidence';
                      const confidenceColor = coveragePercent >= GOOD_SCORE_THRESHOLD
                        ? 'text-green-700 border-green-300 bg-green-50'
                        : coveragePercent >= MEDIUM_SCORE_THRESHOLD
                          ? 'text-yellow-700 border-yellow-300 bg-yellow-50'
                          : 'text-red-700 border-red-300 bg-red-50';
                      return (
                        <>
                          <div className="w-24 h-2.5 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full ${barColor}`}
                              style={{ width: `${coveragePercent}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium text-gray-700">
                            {coveragePercent}%
                          </span>
                          <span className="text-sm text-gray-500">
                            Coverage ({withApps}/{total} requirements matched)
                          </span>
                          <span className={`ml-auto text-xs px-3 py-1 rounded border font-medium ${confidenceColor}`}>
                            {confidenceLabel}
                          </span>
                        </>
                      );
                    })()}
                  </div>
                </div>

                {/* Groundedness Details (collapsible) */}
                <div className="border-t border-gray-200">
                  <button
                    onClick={() => setShowGroundednessDetails(!showGroundednessDetails)}
                    className="w-full p-4 flex items-center justify-between hover:bg-gray-100 transition-colors"
                  >
                    <h5 className="text-sm font-medium text-gray-900">
                      Groundedness Details
                    </h5>
                    <svg
                      className={`w-5 h-5 text-gray-400 transition-transform ${
                        showGroundednessDetails ? 'rotate-180' : ''
                      }`}
                      fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {showGroundednessDetails && (
                    <div className="px-4 pb-4">
                      {(() => {
                        const gaps = displayedCapabilityMappings.filter(
                          (m) => m.matching_applications.length === 0,
                        );
                        if (gaps.length === 0) {
                          return (
                            <p className="text-xs text-gray-500">
                              All requirements have matching applications — no gaps identified.
                            </p>
                          );
                        }
                        return (
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-xs">
                              <ExclamationTriangleIcon className="w-4 h-4 text-orange-600" />
                              <span className="font-medium text-orange-900">
                                {gaps.length} requirement{gaps.length !== 1 ? 's' : ''} with no matching applications
                              </span>
                            </div>
                            <div className="overflow-x-auto">
                              <table className="w-full text-xs border border-orange-200 rounded">
                                <thead className="bg-orange-50">
                                  <tr className="border-b border-orange-200">
                                    <th className="text-left py-2 px-2 font-semibold text-orange-900">
                                      Requirement
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white">
                                  {gaps.map((g) => (
                                    <tr
                                      key={g.requirement}
                                      className="border-b border-orange-100 hover:bg-orange-50 transition-colors"
                                    >
                                      <td className="py-2 px-2 text-orange-800">
                                        {g.requirement}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>

                {/* Execution Plan (collapsible) */}
                <div className="border-t border-gray-200">
                  <ExecutionPlanPanel
                    executionPlan={capabilityMapping.execution_steps ?? null}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Empty State */}
          {!capabilityMapping && !isGeneratingCapabilityMapping && (
            <div className="rounded-lg border border-gray-200 bg-white p-6">
              <div className="py-8 text-center">
                <CubeIcon className="mx-auto mb-4 h-12 w-12 text-gray-400" />
                <h3 className="text-lg font-semibold text-gray-900">
                  No Application Mapping Yet
                </h3>
                <p className="mt-2 text-gray-500">
                  Click Search Apps to find applications for your requirements.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Bottom Navigation */}
        <BottomNavigationBar
          backAction={{
            label: BACK_BUTTON_LABEL,
            onClick: handleBackToContext,
          }}
          primaryActions={[
            {
              label: CONTINUE_BUTTON_LABEL,
              onClick: handleProceedToADRs,
              variant: 'primary',
            },
          ]}
        />

        {/* Error Message Display */}
        {error && (
          <div className="fixed bottom-20 left-1/2 transform -translate-x-1/2 bg-red-50 border border-red-200 text-red-800 px-6 py-4 rounded-lg shadow-lg max-w-2xl z-50">
            <div className="flex items-start gap-3">
              <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">{error}</p>
              </div>
              <button
                onClick={() => setError(null)}
                className="ml-auto text-red-600 hover:text-red-800"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Details Modal */}
        {isDetailsModalOpen && selectedDetails && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[80vh] flex flex-col">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h3 className="text-xl font-semibold text-gray-900">Additional Details</h3>
                <button
                  onClick={handleCloseDetails}
                  className="text-gray-400 hover:text-gray-600 text-2xl font-bold leading-none"
                >
                  ×
                </button>
              </div>
              
              {/* Modal Body */}
              <div className="p-6 overflow-y-auto flex-1">
                <div className="prose max-w-none break-words">
                  <MarkdownText content={selectedDetails} />
                </div>
              </div>
              
              {/* Modal Footer */}
              <div className="flex items-center justify-end p-6 border-t border-gray-200">
                <button
                  onClick={handleCloseDetails}
                  className="px-4 py-2 bg-[#336094] text-white rounded-md hover:bg-[#2a4d7a] transition-colors main-button"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reason Modal for Accept/Skip */}
        {reasonModalOpen && reasonModalData && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div
              className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4"
            >
              {/* Modal Header */}
              <div
                className="flex items-center justify-between p-6 border-b border-gray-200"
              >
                <h2 className="text-xl font-semibold text-gray-900">
                  {reasonModalAction === ACTION_ACCEPT ? 'Accept' : 'Skip'} Application
                </h2>
                <button
                  onClick={() => {
                    setReasonModalOpen(false);
                    setReasonModalData(null);
                    setReasonText('');
                  }}
                  className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
                >
                  ×
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-6">
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-2">
                    <strong>Application:</strong> {reasonModalData.app.name}
                  </p>
                  <p className="text-sm text-gray-500">
                    {reasonModalData.app.app_number} • {reasonModalData.app.portfolio}
                  </p>
                </div>

                <div className="mb-4">
                  <label
                    htmlFor="reason"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Reason for {reasonModalAction === ACTION_ACCEPT ? 'accepting' : 'skipping'}
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <textarea
                    id="reason"
                    value={reasonText}
                    onChange={(e) => setReasonText(e.target.value)}
                    placeholder={
                      reasonModalAction === ACTION_ACCEPT
                        ? 'Explain why this application is suitable...'
                        : 'Explain why this application is not suitable...'
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm
                      focus:ring-[#336094] focus:border-[#336094] min-h-[120px] resize-y"
                    required
                  />
                  {!reasonText.trim() && (
                    <p className="text-xs text-red-500 mt-1">
                      Please provide a reason for your decision
                    </p>
                  )}
                </div>
              </div>

              {/* Modal Footer */}
              <div
                className="flex items-center justify-end gap-3 p-6 border-t border-gray-200"
              >
                <button
                  onClick={() => {
                    setReasonModalOpen(false);
                    setReasonModalData(null);
                    setReasonText('');
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700
                    hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitReason}
                  disabled={!reasonText.trim()}
                  className={`px-4 py-2 rounded-md transition-colors font-medium ${
                    reasonModalAction === ACTION_ACCEPT
                      ? 'bg-[#FFD700] text-gray-900 hover:bg-[#FFC700] disabled:bg-gray-200'
                      : 'bg-gray-600 text-white hover:bg-gray-700 disabled:bg-gray-300'
                  } disabled:cursor-not-allowed`}
                >
                  {reasonModalAction === ACTION_ACCEPT ? '✓ Accept & Add' : '✕ Skip'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
